/**
 * FTC REPORT BUILDER PAGE
 * Standalone page accessible from main dashboard
 * 
 * Flow:
 * 1. Select Previous Client OR Create New
 * 2. Enter/Paste Client Info
 * 3. Paste Accounts 
 * 4. FTC Builder (same as Dispute Wizard)
 */

import ClientInfoEditor, { ClientInfoCheck, toCheck } from '../components/ClientInfoEditor';
import FtcEditChoiceModal from '../components/FtcEditChoiceModal';
import { checkClient } from '../lib/ftcPreflight';
import { normalizeDatesInText, parseDateOnly } from '../lib/smartDate';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, ArrowLeft, ArrowRight, Trash2, Plus, PlusCircle, User, ClipboardList, Settings, ClipboardPaste, Loader2, Calendar, CalendarDays, Sparkles, Upload, AlertTriangle
} from 'lucide-react';
import FTCReportBuilderNew from '../components/FTCReportBuilderNew';
import BatchLettersModal from '../components/BatchLettersModal';
import MobileNav from '../components/MobileNav';
import SavedFTCReportsGallery from '../components/SavedFTCReportsGallery';
import ArchiveClients from '../components/ArchiveClients';
import AccountExtractorStep from '../components/AccountExtractorStep';
import EditSectionsWalkthrough from '../components/EditSectionsWalkthrough';
import AccountAbbreviationModal from '../components/AccountAbbreviationModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FTCBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeReportId } = useParams();  // /ftc-builder/:id loads a saved report
  
  // Step: 1 = Client Info, 2 = Paste Accounts, 3 = FTC Builder
  const [step, setStep] = useState(1);
  const [galleryKey, setGalleryKey] = useState(0);

  // Feb 2026 test harness — ?previewPostDispute=1 jumps straight to
  // step 3 so FTCReportBuilderNew's own preview effect can fire.  Used
  // by the automated test that verifies the bureau-forms prompt opens
  // after the DisputeLetterModal closes.  Guarded behind a URL flag so
  // it never runs for real users.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('previewPostDispute') === '1' || qs.get('previewLetterTypes') === '1') setStep(3);
  }, []);
  
  // Sections snapshot from a loaded saved report — passed through to
  // FTCReportBuilderNew so it can restore the per-section accountIds, fraud
  // type and custom total without the user having to re-categorize anything.
  const [savedReportSections, setSavedReportSections] = useState(null);
  // 2026-02-28 — Track the client_id of the currently-loaded saved
  // report so the walkthrough's Add/Replace picker can fetch the
  // pool of the client's other accounts.
  const [savedReportClientId, setSavedReportClientId] = useState(null);
  const [savedReportId, setSavedReportId] = useState(routeReportId || null);
  const [loadingSavedReport, setLoadingSavedReport] = useState(!!routeReportId);

  // 2026-02-08 — 3-way edit prompt (per user request). When the user
  // opens a saved report, ask what they want to do before dumping them
  // into Step 3:
  //   • Edit as-is (default)
  //   • ADD new accounts to an open slot (only enabled if any of the
  //     5 sections is empty)
  //   • REPLACE a specific section (1-5) with new accounts
  // The choice sets `editIntent` and jumps the user to the right step.
  const [editIntent, setEditIntent] = useState(null); // 'edit'|'append'|'replace'|null
  const [replaceSectionNum, setReplaceSectionNum] = useState(null);
  const [showEditIntentModal, setShowEditIntentModal] = useState(false);
  // 2026-02-28 — Section-by-section walkthrough overlay (Keep / Remove /
  // Replace per account, section-by-section).  Fires from the edit-intent
  // modal's new "Walk through each section" option.
  const [showSectionWalkthrough, setShowSectionWalkthrough] = useState(false);

  // 2026-02-08 — When the user taps "FTC Report" from the dashboard we
  // ship them here via `?fresh=1`. Instead of dumping them straight into
  // the empty Step-1 client-info form, we show a clean picker: a big
  // "Start New Report" button at the top and the full list of previously
  // saved reports below. Clicking Start New reveals the form; clicking
  // any saved report opens it with the 3-way edit prompt.
  const [showStep1Form, setShowStep1Form] = useState(true);

  // Feb 2026 (per user request): the "What do you want to do?"
  // extract/merge/replace prompt after loading a saved report was removed.
  // Users now land directly on Step 3 (Build Report) which already shows
  // every section as an editable summary.  State is kept in case future
  // callers still setShowEditExtractPrompt(null) — harmless.

  // FTC Report Theme: 'color' (official FTC colors - DEFAULT) or 'bw' (black & white)
  const [ftcTheme, setFtcTheme] = useState('color');
  
  // Date Format for FTC Report:
  // 'month_year' (DEFAULT) = "08-2026"    — zero-padded month, year only
  // 'full_date'             = "08-15-2026" — zero-padded month, day, and year
  //
  // Per user request 2026-06-05 (final clarification): open date is ALWAYS
  // MM-YYYY — the parser strips the day even if the source provides it. The
  // toggle still exists for one-off reports that want the day back.
  const [dateFormat, setDateFormat] = useState('month_year');
  
  // ============================================================
  // Persistent state — Feb 2026 user request: pressing "Back" (either
  // the app-header arrow or the browser back button) must NOT lose the
  // client info, pasted accounts, or current step.  We snapshot the
  // whole new-client flow into sessionStorage on every state change
  // and rehydrate on mount.  Saved-report (routeReportId) flows skip
  // this — those are already server-persisted.
  // ============================================================
  const PERSIST_KEY = 'ftc_builder_persistent_state_v1';

  // 2026-02-08 — user reported the page auto-scrolls to the bottom on
  // open. Force the browser to the top on every mount so the client-info
  // form / picker view is always what you see first.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Rehydrate on first mount BEFORE any effects that would overwrite state.
  // NOTE: React state initializers only run once, so it is safe to read
  // sessionStorage inside useState via a lazy initializer above — but we
  // want to leave the existing state initializers alone.  Instead we use
  // a single "hydrate once" effect that runs synchronously on mount.
  const [hydratedFromSession, setHydratedFromSession] = useState(false);
  useEffect(() => {
    if (routeReportId) { setHydratedFromSession(true); return; }
    // 2026-02-08 — When the user taps "FTC Report" from the dashboard we
    // pass `?fresh=1` to force a brand-new report. Wipe every persisted
    // key so the wizard opens empty on Step 1 instead of restoring the
    // last half-finished draft.
    const params = new URLSearchParams(location.search || '');
    if (params.get('fresh') === '1') {
      try {
        sessionStorage.removeItem(PERSIST_KEY);
        sessionStorage.removeItem('ftc_builder_pending_clientinfo');
        sessionStorage.removeItem('ftc_builder_append_mode');
        sessionStorage.removeItem('ftc_builder_existing_accounts_text');
      } catch { /* ignore */ }
      // Land on the picker (gallery + big Start New) — the client-info
      // form stays hidden until user commits to a brand-new report.
      setShowStep1Form(false);
      navigate('/ftc-builder', { replace: true });
      setHydratedFromSession(true);
      return;
    }
    try {
      const raw = sessionStorage.getItem(PERSIST_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s && typeof s === 'object') {
          if (s.step) setStep(s.step);
          if (s.clientInfo) setClientInfo((prev) => ({ ...prev, ...s.clientInfo }));
          if (s.pasteClientText) setPasteClientText(s.pasteClientText);
          if (s.pastedAccountsText) setPastedAccountsText(s.pastedAccountsText);
          if (s.ftcTheme) setFtcTheme(s.ftcTheme);
          if (s.dateFormat) setDateFormat(s.dateFormat);
        }
      }
    } catch { /* ignore corrupt state */ }
    setHydratedFromSession(true);
  }, []);

  // Client Info
  const [clientInfo, setClientInfo] = useState({
    fullName: '',
    dateOfBirth: '',
    ssn: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: ''
  });
  
  // 2026-02-08 — Required-field validation state for Client Info section.
  // Tracks which required fields are empty when the user clicks Continue so
  // we can light them up in red and block progression.
  const [missingFields, setMissingFields] = useState(new Set());

  // 2026-02-28 — Duplicate-report early-detect state.
  // As the user types the client's name (Step 1) we debounce a lookup
  // against /api/ftc-reports/lookup and surface a small warning banner
  // if a report for that name+email already exists.  The banner has
  // an "Open existing report" button that loads the report into the
  // builder so the user can pick up where they left off.
  const [existingReportMatch, setExistingReportMatch] = useState(null);

  // Paste text for client info
  const [pasteClientText, setPasteClientText] = useState('');
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [keptAccounts, setKeptAccounts] = useState([]); // saved report's accounts kept during "add new" edits

  // Feb 2026 (user request): our own cover-letter header ("ATTN: FRAUD
  // DISPUTE DEPARTMENT") often ends up at the top of pasted / uploaded
  // client info because that's the format most agents copy from.  Strip
  // it out on the way in so it never confuses the parser or has to be
  // deleted manually.  Only touches recognisable header noise —
  // personal fields (name, DOB, SSN, address, phone, email) are left
  // untouched no matter where they sit.
  const _stripPacketCoverNoise = (raw) => {
    if (!raw) return raw;
    const NOISE = [
      /^\s*attn[:.]?\s*$/i,
      /^\s*attn[:.]?\s*fraud[\s_-]*(dispute)?[\s_-]*(department|dept)\s*$/i,
      /^\s*fraud[\s_-]*(dispute)?[\s_-]*(department|dept)\s*$/i,
      /^\s*(re|subject)[:.]?\s*identity[\s_-]*theft.*$/i,
      /^\s*cover[\s_-]*letter\s*$/i,
    ];
    const lines = raw.split(/\r?\n/);
    // Drop any consecutive header-noise lines from the TOP, plus any
    // stray occurrences later in the text.
    let firstReal = 0;
    while (firstReal < lines.length && (!lines[firstReal].trim() || NOISE.some(rx => rx.test(lines[firstReal])))) {
      firstReal += 1;
    }
    const rest = lines.slice(firstReal).filter(ln => !NOISE.some(rx => rx.test(ln)));
    return rest.join('\n');
  };
  const setPasteClientTextScrubbed = (v) => setPasteClientText(_stripPacketCoverNoise(v));
  
  // Parse client info from pasted text
  // Supports multiple formats:
  // FORMAT A (7 lines): Name, DOB, SSN, Address, City State Zip, Phone, Email
  // FORMAT B (9 lines): Name, DOB, SSN, Address, City, State, Zip, Phone, Email
  //
  // Robust to full state names: "Los Angeles California 90001" / "Los Angeles, CA 90001"
  // Robust to missing zip: "Los Angeles California" (state still extracted)
  const US_STATE_NAME_TO_ABBR = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'district of columbia': 'DC', 'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI',
    'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
    'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME',
    'maryland': 'MD', 'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN',
    'mississippi': 'MS', 'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE',
    'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
    'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
    'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI',
    'south carolina': 'SC', 'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX',
    'utah': 'UT', 'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA',
    'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
    'puerto rico': 'PR',
  };
  const ALL_STATE_ABBRS = new Set(Object.values(US_STATE_NAME_TO_ABBR));

  // Pull out city / 2-letter state / zip from a single string like:
  //   "Los Angeles California 90001"  →  { city: 'Los Angeles', state: 'CA', zip: '90001' }
  //   "Los Angeles, CA 90001"          →  { city: 'Los Angeles', state: 'CA', zip: '90001' }
  //   "New York City, New York"        →  { city: 'New York City', state: 'NY', zip: '' }
  //   "Saint Louis MO"                 →  { city: 'Saint Louis', state: 'MO', zip: '' }
  const extractCityStateZip = (raw) => {
    if (!raw) return { city: '', state: '', zip: '' };
    let s = String(raw).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();

    // 1) Pull a trailing ZIP if present.
    let zip = '';
    const zipMatch = s.match(/\b(\d{5}(?:-\d{4})?)\s*$/);
    if (zipMatch) {
      zip = zipMatch[1];
      s = s.slice(0, zipMatch.index).trim();
    }

    // 2) Try: trailing 2-letter state abbr.
    let state = '';
    let city = s;
    const abbrMatch = s.match(/\b([A-Za-z]{2})\s*$/);
    if (abbrMatch && ALL_STATE_ABBRS.has(abbrMatch[1].toUpperCase())) {
      state = abbrMatch[1].toUpperCase();
      city = s.slice(0, abbrMatch.index).trim();
    } else {
      // 3) Try matching a FULL state name (longest first so "New York" beats "York").
      const stateNames = Object.keys(US_STATE_NAME_TO_ABBR).sort((a, b) => b.length - a.length);
      const lower = s.toLowerCase();
      for (const name of stateNames) {
        if (lower.endsWith(name)) {
          state = US_STATE_NAME_TO_ABBR[name];
          city = s.slice(0, s.length - name.length).trim();
          break;
        }
      }
    }
    return { city: city || '', state: state || '', zip: zip || '' };
  };

  const parseClientInfo = (text) => {
    // 2026-02-08 — Pattern-based parser (per user request). Instead of
    // trusting line order, we scan every line and pick the first one
    // that MATCHES each field's shape. This fixes:
    //   • SSN on line 2 (user pastes it out of order)
    //   • Phone missing entirely (single-line CityStateZip w/o phone)
    //   • "City ST ZIP" all on one line (e.g. "LAKESIDE CA 92040")
    //   • Boilerplate lines like "ATTN: FRAUD DISPUTE DEPARTMENT"
    const rawLines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l);
    const parsed = { fullName: '', dateOfBirth: '', ssn: '', address: '',
      city: '', state: '', zipCode: '', phone: '', email: '' };

    // Field-shape regexes.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
    const ssnRe   = /^\d{3}-?\d{2}-?\d{4}$/;
    // Phone: 10 digits after stripping formatting.
    const isPhoneLike = (s) => {
      if (/[a-z]/i.test(s)) return false; // "132 W NEES PMB 2067 SUITE 106" has 10 digits but is an address
      const digits = s.replace(/\D/g, '');
      return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
    };
    // DOB: MM/DD/YYYY or MM-DD-YYYY (year 1900-2099).
    const dobRe = /^(0?[1-9]|1[0-2])[\/\-\.](0?[1-9]|[12]\d|3[01])[\/\-\.](19|20)\d{2}$/;
    // Address: starts with 1+ digits + street tokens.
    const addrRe = /^\d+\s+\S+.*(street|st\.?|ave|avenue|blvd|boulevard|road|rd\.?|drive|dr\.?|lane|ln\.?|court|ct\.?|way|pl\.?|place|circle|cir\.?|hwy|highway|pkwy|parkway|trail|terrace|ter|apt|unit|suite|ste|pmb|loop|row|sq|#)/i;
    const looseAddrRe = /^(\d+[a-z]?\s+[a-z]|p\.?\s*o\.?\s*box)/i; // house number then letters
    // City / State / ZIP on one line — Feb 2026: we look for ANY line
    // that ends in a 5-digit ZIP and try to decode city + state from
    // whatever sits in front of it.  The old regex only accepted
    // 2-letter abbrs (e.g. "LAKESIDE CA 92040") and silently dropped
    // rows like "SAN ANTONIO Texas 78253" — extractCityStateZip
    // handles BOTH abbrs and spelled-out state names ("Texas",
    // "North Carolina", etc.), so we call it as the primary matcher
    // and only fall back to the strict abbr regex for perf.
    const zipEndRe = /\b\d{5}(?:-\d{4})?\s*$/;
    // Line the user probably wants ignored.
    const boilerplateRe = /^(attn\s*[:\-]|to\s*whom|fraud\s+dispute\s+department|re\s*[:\-])/i;

    const used = new Set();
    const take = (idx) => { used.add(idx); };
    // Labeled lines ("DOB: August 27, 1984", "Phone: (559) 573-2744") go straight to their field.
    const LABELS = [
      [/^(?:full\s*)?name/i, 'fullName'], [/^(?:dob|d\.o\.b\.?|date\s*of\s*birth|birth\s*date)/i, 'dateOfBirth'],
      [/^(?:ssn|ss#|ss\s*no\.?|social(?:\s*security)?(?:\s*(?:number|no\.?|#))?)/i, 'ssn'],
      [/^(?:phone|cell|mobile|tel(?:ephone)?)(?:\s*(?:number|no\.?|#))?/i, 'phone'], [/^e-?mail(?:\s*address)?/i, 'email'],
      [/^(?:street\s*)?address(?:\s*1)?/i, 'address'], [/^city/i, 'city'], [/^state/i, 'state'], [/^zip(?:\s*code)?/i, 'zipCode'],
    ];
    rawLines.forEach((line, idx) => {
      const m = line.match(/^([A-Za-z.#\s-]{2,30}?)\s*[:#=-]\s*(.+)$/);
      if (!m) return;
      const hit = LABELS.find(([re]) => re.test(m[1].trim()) && m[1].trim().match(re)[0].length === m[1].trim().length);
      if (!hit) return;
      let v = m[2].trim();
      if (hit[1] === 'dateOfBirth') { const d = parseDateOnly(v); if (d && d.length === 10) v = d.replace(/-/g, '/'); }
      if (hit[1] === 'state') v = v.toUpperCase();
      if (!parsed[hit[1]]) { parsed[hit[1]] = v; rawLines[idx] = ''; take(idx); }
    });

    // Pass 1: pick off unambiguous shapes.
    rawLines.forEach((line, idx) => {
      if (used.has(idx) || boilerplateRe.test(line)) { if (boilerplateRe.test(line)) used.add(idx); return; }
      if (!parsed.email && emailRe.test(line)) { parsed.email = line; take(idx); return; }
      if (!parsed.ssn && ssnRe.test(line.replace(/\s/g, ''))) {
        const d = line.replace(/\D/g, '');
        parsed.ssn = `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
        take(idx); return;
      }
      if (!parsed.dateOfBirth && (dobRe.test(line) || parseDateOnly(line))) {
        const d = parseDateOnly(line);
        parsed.dateOfBirth = d && d.length === 10 ? d.replace(/-/g, '/') : line; // "August 27, 1984" / "08271984" → 08/27/1984
        take(idx); return;
      }
      // City / State / ZIP on one line — supports both abbreviation
      // ("LAKESIDE CA 92040") and spelled-out state ("SAN ANTONIO
      // Texas 78253") thanks to extractCityStateZip's dual matcher.
      if (!parsed.zipCode && zipEndRe.test(line)) {
        const csz = extractCityStateZip(line);
        if (csz.state && csz.zip) {
          parsed.city = csz.city;
          parsed.state = csz.state;
          parsed.zipCode = csz.zip;
          take(idx);
          return;
        }
      }
      if (!parsed.address && addrRe.test(line)) { parsed.address = line; take(idx); return; }
    });
    rawLines.forEach((line, idx) => {
      if (!used.has(idx) && !parsed.address && looseAddrRe.test(line) && !zipEndRe.test(line)) { parsed.address = line; take(idx); }
    });

    // Pass 2: phone (only after SSN so we don't confuse them). Anything
    // that's 10-11 digits and has NOT been claimed already.
    rawLines.forEach((line, idx) => {
      if (used.has(idx) || parsed.phone) return;
      if (isPhoneLike(line) && !ssnRe.test(line.replace(/\s/g, ''))) {
        parsed.phone = line;
        take(idx);
      }
    });

    // Pass 3: whatever's left. First remaining line that's all letters (no
    // digits) is the name; second remaining line — if we still have no
    // address AND it has any digits — becomes the address; anything else
    // fills in city if that field is still blank.
    for (const [idx, line] of rawLines.entries()) {
      if (used.has(idx)) continue;
      const hasDigit = /\d/.test(line);
      if (!parsed.fullName && !hasDigit && /[A-Za-z]/.test(line)) {
        parsed.fullName = line;
        take(idx);
        continue;
      }
      if (!parsed.address && hasDigit && /[a-z]/i.test(line)) {
        parsed.address = line;
        take(idx);
        continue;
      }
      if (!parsed.city && !hasDigit) {
        parsed.city = line;
        take(idx);
      }
    }

    // If separate city/state/zip lines were given (rare), try to salvage them.
    if (!parsed.zipCode) {
      const zipLine = rawLines.find(l => /^\d{5}(?:-\d{4})?$/.test(l));
      if (zipLine) parsed.zipCode = zipLine;
    }
    if (!parsed.state) {
      const stLine = rawLines.find(l => /^[A-Za-z]{2}$/.test(l) && ALL_STATE_ABBRS.has(l.toUpperCase()));
      if (stLine) parsed.state = stLine.toUpperCase();
      else {
        const stFullLine = rawLines.find(l => US_STATE_NAME_TO_ABBR[l.toLowerCase()]);
        if (stFullLine) parsed.state = US_STATE_NAME_TO_ABBR[stFullLine.toLowerCase()];
      }
    }

    return parsed;
  };
  

  // Handle parse button click
  const handleParseClientInfo = () => {
    if (!pasteClientText.trim()) {
      toast.error('Please paste client info first');
      return;
    }
    const parsed = parseClientInfo(pasteClientText);
    setClientInfo(parsed);
    if (parsed.fullName) {
      toast.success(`Parsed info for: ${parsed.fullName}`);
    } else {
      toast.error('Could not detect the client info — try adding line breaks between fields.');
    }
  };

  // 2026-02-08 — Drag/drop or upload a PDF/TXT of client info. Reads .txt
  // client-side; POSTs .pdf to /api/parse-personal-info-file so PyMuPDF
  // can extract the raw text. Extracted text lands in the textarea and
  // auto-parses in the same tick.
  const handleUploadClientInfoFile = async (file) => {
    try {
      let text = '';
      if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
        text = await file.text();
      } else {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/api/parse-personal-info-file`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
        text = data?.text || '';
      }
      if (!text.trim()) {
        toast.error('No text could be extracted from that file');
        return;
      }
      const cleanedText = _stripPacketCoverNoise(text);
      setPasteClientText(cleanedText);
      const parsed = parseClientInfo(cleanedText);
      setClientInfo((prev) => ({ ...prev, ...parsed }));
      toast.success(`Extracted ${cleanedText.length} chars and auto-parsed the fields`);
    } catch (e) {
      console.error('[client-info upload]', e);
      toast.error(e?.response?.data?.detail || 'Failed to read that file');
    }
  };
  
  // Pasted accounts
  const [pastedAccountsText, setPastedAccountsText] = useState('');
  const [parsedAccounts, setParsedAccounts] = useState([]);
  // "Add new" edits: keep the saved accounts and drop the new ones into the empty slots (first open slot).
  const keptIds = new Set(keptAccounts.map((a) => a.id));
  const newOnes = editIntent === 'append' ? parsedAccounts.filter((a) => !keptIds.has(a.id)) : [];
  const builderAccounts = editIntent === 'append' ? [...keptAccounts, ...newOnes] : parsedAccounts;
  // New accounts stay unassigned so the user places each one; slots holding kept accounts are locked.
  const builderSections = savedReportSections;
  const lockedSlots = editIntent === 'append' && savedReportSections
    ? [1, 2, 3, 4, 5].filter((n) => ((savedReportSections[n] || savedReportSections[String(n)])?.accounts || []).length)
    : [];
  const [letterSuggestOpen, setLetterSuggestOpen] = useState(false);
  const [letterSuggestAccounts, setLetterSuggestAccounts] = useState([]);
  // Shorten-&-merge modal (2026-03-01) — opens automatically after
  // successful account parse, and also on-demand via the toolbar button.
  const [abbrevModalOpen, setAbbrevModalOpen] = useState(false);
  // Feb 2026 fix — the ASB → FTC prefill path used to drop the
  // extractor's per-account categories on the floor, so when the user
  // clicked "Parse Accounts" everything defaulted to OTHER in the
  // Quick Reference sidebar.  We now stash the extractor payload here
  // and thread it into every parseAccounts() invocation so categories
  // survive the round-trip.  Also used by the orange Parse button (which
  // takes no explicit args).
  const [pendingExtractorAccounts, setPendingExtractorAccounts] = useState(null);
  // Feb 2026 UX request — the user wants an at-a-glance confirmation of
  // exactly how the accounts will be labeled and grouped in the final
  // dispute-letter PDF, BEFORE that PDF is generated. `showDisputeLayoutPreview`
  // toggles a modal that renders the parsed accounts in the same visual
  // format the ReportLab pipeline will produce (bold category header +
  // bold account name — date — $amount bullets).
  const [showDisputeLayoutPreview, setShowDisputeLayoutPreview] = useState(false);

  // Persist snapshot on any change once initial hydration is complete.
  // Skips saved-report flows (server-persisted) to avoid stomping over
  // route-based state.
  useEffect(() => {
    if (!hydratedFromSession || routeReportId) return;
    try {
      sessionStorage.setItem(PERSIST_KEY, JSON.stringify({
        step,
        clientInfo,
        pasteClientText,
        pastedAccountsText,
        ftcTheme,
        dateFormat,
      }));
    } catch { /* quota exceeded — ignore */ }
  }, [hydratedFromSession, routeReportId, step, clientInfo, pasteClientText, pastedAccountsText, ftcTheme, dateFormat]);

  // 2026-02-28 — Debounced duplicate-report lookup.  Fires 600ms after
  // the user stops typing in the Name / Email fields on Step 1.  If a
  // saved report already exists for that (name, email) combo, we
  // surface a warning banner with a one-tap "Open existing report"
  // button.  We only run the check when the user is on Step 1 AND
  // hasn't already loaded a specific report via a route param — no
  // point spamming this when they're editing an existing one.
  useEffect(() => {
    if (step !== 1 || routeReportId) {
      setExistingReportMatch(null);
      return;
    }
    const name = (clientInfo.fullName || '').trim();
    const email = (clientInfo.email || '').trim();
    if (name.length < 4) {
      setExistingReportMatch(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ name });
        if (email) params.set('email', email);
        const r = await axios.get(`${API}/api/ftc-reports/lookup?${params.toString()}`);
        if (!cancelled) {
          setExistingReportMatch(r.data?.exists ? r.data.report : null);
        }
      } catch (_e) {
        if (!cancelled) setExistingReportMatch(null);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [step, routeReportId, clientInfo.fullName, clientInfo.email]);

  // Helper: Clean and format amount (remove commas, ensure $ prefix, no decimals)
  const cleanAmount = (amountStr) => {
    if (!amountStr) return '';
    // Extract just the number, removing $, commas, and any trailing text
    const match = amountStr.match(/\$?([\d,]+(?:\.\d{2})?)/);
    if (match) {
      // Remove commas and convert to integer (no cents)
      const cleanNum = parseInt(match[1].replace(/,/g, '').replace(/\.\d+$/, ''));
      return `$${cleanNum}`;
    }
    return amountStr;
  };
  
  // Helper: Format date with dashes (convert / to -)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Normalize: convert slashes to dashes
    let cleaned = dateStr.replace(/\//g, '-').trim();
    
    // Always strip day when month_year mode
    if (dateFormat === 'month_year') {
      // Handle MM-DD-YYYY
      const fullMatch = cleaned.match(/^(\d{1,2})-\d{1,2}-(\d{4})$/);
      if (fullMatch) return `${fullMatch[1]}-${fullMatch[2]}`;
      // Handle YYYY-MM-DD
      const isoMatch = cleaned.match(/^(\d{4})-(\d{1,2})-\d{1,2}$/);
      if (isoMatch) return `${isoMatch[2]}-${isoMatch[1]}`;
    }
    
    return cleaned;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Canonical date formatter — used EVERYWHERE so output is identical regardless
  // of how the date was pasted or parsed.
  //   month_year → "08-2026"     (always 2-digit month)
  //   full_date  → "08-15-2026"  (always 2-digit month and 2-digit day)
  //
  // Accepts ANY of these inputs and normalizes them:
  //   "8-2026", "08-2026", "8/2026", "08/2026"        → MM-YYYY (no day available)
  //   "8-15-2026", "08-15-2026", "8/15/2026", "08/15/2026", "2026-08-15"
  // ─────────────────────────────────────────────────────────────────────────────
  const normalizeDateForFormat = (rawDate, targetFormat) => {
    if (!rawDate) return '';
    const cleaned = String(rawDate).replace(/[\/.]/g, '-').replace(/\s+/g, '').trim();
    const pad2 = (n) => String(parseInt(n, 10)).padStart(2, '0');

    // Try MM-DD-YYYY (or M-D-YYYY)
    let m = cleaned.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (m) {
      const month = pad2(m[1]);
      const day = pad2(m[2]);
      const year = m[3];
      return targetFormat === 'full_date'
        ? `${month}-${day}-${year}`
        : `${month}-${year}`;
    }
    // Try YYYY-MM-DD (ISO)
    m = cleaned.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) {
      const year = m[1];
      const month = pad2(m[2]);
      const day = pad2(m[3]);
      return targetFormat === 'full_date'
        ? `${month}-${day}-${year}`
        : `${month}-${year}`;
    }
    // Try MM-YYYY (no day)
    m = cleaned.match(/^(\d{1,2})-(\d{4})$/);
    if (m) {
      const month = pad2(m[1]);
      const year = m[2];
      // No day available — both modes render MM-YYYY
      return `${month}-${year}`;
    }
    return cleaned; // unknown shape — return as-is
  };
  
  // Parse pasted accounts
  // Supports formats:
  // 1. CREDITOR NAME-MM-DD-YYYY-$AMOUNT (user's preferred format)
  // 2. Multiple accounts on same line - auto-detect and split
  // 3. Comma-separated format
  const parseAccounts = (opts = {}) => {
    // Allow callers (e.g. the AI Extractor handoff) to pass the text
    // explicitly so we don't race against React's setState commit.
    const sourceText = (opts.overrideText != null ? opts.overrideText : pastedAccountsText) || '';
    if (!sourceText.trim()) {
      toast.error('Please paste account information');
      return;
    }
    
    let text = sourceText.trim();
    const accounts = [];

    // Build a quick lookup of category by canonical "NAME|MM-YYYY" key so
    // accounts arriving from the AI Extractor keep their category through to
    // the Parsed Accounts list (no re-detection guesswork).
    // Feb 2026 fix — also fall back to any extractor payload stashed by
    // the ASB → FTC prefill so the orange "Parse Accounts" button gets
    // the same category map even when no options are passed in.
    const extractorByKey = new Map();
    const rawExtractor = Array.isArray(opts.extractorAccounts) && opts.extractorAccounts.length
      ? opts.extractorAccounts
      : (Array.isArray(pendingExtractorAccounts) ? pendingExtractorAccounts : null);
    if (Array.isArray(rawExtractor)) {
      for (const a of rawExtractor) {
        const name = (a.account_name || '').toUpperCase().trim();
        let date = (a.date_opened || '').trim();
        const dm = date.match(/^(\d{2})-\d{2}-(\d{4})$/);
        if (dm) date = `${dm[1]}-${dm[2]}`;
        extractorByKey.set(`${name}|${date}`, a);
      }
    }

    // Strip section-header lines like "AUTO ACCOUNTS (3)" or plain ASB
    // category labels like "Credit Card" / "Auto Loan" / "Personal Loan"
    // that arrive from the ASB → Step 2 handoff (Feb 2026 UX request:
    // headers stay visible in the textarea so the user can see which
    // section each account belongs to, but they're skipped for parsing).
    const HEADING_RE = /^(AUTO(?:\s+LOAN)?|CREDIT\s*CARD|CREDIT\s*UNION|PERSONAL\s+LOAN|STUDENT\s+LOAN|MORTGAGE|APARTMENT\s+LEASE|RENTAL\s+LEASE|UTILITY|MEDICAL|COLLECTION|OTHER|RETAIL\s+CARD|REPOSSESSION|INSTALLMENT\s+LOAN|BUY\s+NOW\s+PAY\s+LATER|TELECOM|MOBILE|CHILD\s+SUPPORT|TIMESHARE)(?:\s+ACCOUNTS?)?(?:\s*\(\d+\))?\s*$/i;
    text = text.split(/\r\n|\r|\n/).filter((rawLine) => {
      return !HEADING_RE.test(rawLine.trim());
    }).map(normalizeDatesInText).join("\n"); // any written date → MM-DD-YYYY / MM-YYYY

    // Per user request 2026-06-05: tolerate "messy" lines where the user
    // duplicates the amount and date after a comma, e.g.
    //   AMER CR ACPT- JAN. 17, 2024- $41640, , $41640.00, 01/17/2024
    // Strategy: only chop tails that look like junk (e.g., $amt, , $amt, date)
    // — detected by the pattern ", , $" or ", $...,". We keep simple
    // thousand-separator commas inside amounts (e.g., $12,500) intact.
    text = text.split(/\r\n|\r|\n/).map((rawLine) => {
      const line = rawLine.trim();
      if (!line) return line;
      // Look for the junk-tail signature: "..., (optional blanks), $...".
      // This guarantees we only chop when there are clearly TWO dollar
      // amounts on the line.
      const dollarCount = (line.match(/\$/g) || []).length;
      if (dollarCount < 2) return line;
      const m = line.match(/^(.*?\$[\d,]+(?:\.\d{2})?),\s*(?:,\s*)?\$/);
      if (m) {
        return m[1].trim();
      }
      return line;
    }).join("\n");
    
    // SMART SPLIT: Detect accounts in format CREDITOR-MM-DD-YYYY-$AMOUNT
    // Split when we see: $AMOUNT followed by space and then a letter (new creditor)
    // Pattern: $XXX or $X,XXX followed by space and uppercase letter
    const smartSplitPattern = /(\$[\d,]+(?:\.\d{2})?)\s+(?=[A-Z])/g;
    
    // Replace the pattern with a newline to split accounts
    text = text.replace(smartSplitPattern, '$1\n');
    
    // Normalize line endings and split - handle \r\n, \r, and \n
    const lines = text.split(/\r\n|\r|\n/).map(line => line.trim()).filter(line => line.length > 0);
    
    console.log(`FTCBuilderPage: Total lines after split: ${lines.length}`);
    console.log(`FTCBuilderPage: Lines:`, lines);
    
    lines.forEach((line, index) => {
      let trimmed = line.trim();
      console.log(`Parsing line ${index}: "${trimmed}"`);
      
      if (trimmed && trimmed.length > 2) {
        const lowerLine = trimmed.toLowerCase();
        // Skip header lines
        if (lowerLine.includes('creditor') && lowerLine.includes('name') || 
            lowerLine.includes('account name') || 
            lowerLine.includes('---') ||
            lowerLine.startsWith('previous creditor:')) {
          console.log(`  Skipping header line`);
          return;
        }
        
        let dateOpened = '';
        let rawFullDate = '';  // For toggle reversibility: keep MM-DD-YYYY when day is known
        let accountNumber = '';
        let fraudAmount = '';
        
        // I had `creditorName` declared above. Fix: re-declare here cleanly
        let creditorName = '';

        // 2026-02-XX (bugfix) — Comma-first path.  When the line has a
        // clear comma-separated shape (2+ commas), parse it as CSV FIRST
        // before the regex chain.  Previously the regex chain would
        // capture the trailing date but leave the middle "$1500.00" stuck
        // to the creditor name and null out fraudAmount, so every
        // comma-separated paste (e.g. "CAPITAL ONE, $1500.00, 09/15/2024")
        // ended up with $0 total fraud and an ugly ", $1500.00" suffix
        // on the creditor name.
        const commaCount = (trimmed.match(/,/g) || []).length;
        let handledByComma = false;
        if (commaCount >= 2) {
          const commaParts = trimmed.split(/[,\t]+/).map(p => p.trim()).filter(Boolean);
          if (commaParts.length >= 2) {
            // Heuristic: an "amount piece" starts with $ or is a bare
            // number ("1500" or "1500.00"); a "date piece" matches a
            // full or partial date pattern.
            // When any piece has a "$", only "$" pieces count as amounts (bare numbers are acct #s).
            const hasDollar = commaParts.slice(1).some(p => p.includes('$'));
            const isAmountPiece = (p) => (hasDollar ? /^\$\s*[\d,]+(?:\.\d{1,2})?\s*$/ : /^\$?\s*[\d,]+(?:\.\d{1,2})?\s*$/).test(p);
            const isDatePiece = (p) => /\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}/.test(p) ||
                                        /\d{1,2}[-\/]\d{4}/.test(p) ||
                                        /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(p);

            // Name is always first piece; the remaining pieces get
            // classified individually so column order doesn't matter.
            creditorName = commaParts[0];
            for (const p of commaParts.slice(1)) {
              if (!fraudAmount && isAmountPiece(p)) {
                fraudAmount = cleanAmount(p);
              } else if (!dateOpened && isDatePiece(p)) {
                const parsedDate = formatDate(p);
                dateOpened = parsedDate;
                // If it was a full date, keep it for toggle reversibility.
                const fullMatch = p.match(/(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
                if (fullMatch) {
                  rawFullDate = normalizeDateForFormat(
                    `${fullMatch[1]}-${fullMatch[2]}-${fullMatch[3]}`,
                    'full_date',
                  );
                  dateOpened = normalizeDateForFormat(rawFullDate, dateFormat);
                }
              } else if (!accountNumber) {
                accountNumber = p;
              }
            }
            handledByComma = true;
            console.log(`  Comma-first parse → name="${creditorName}", amount="${fraudAmount}", date="${dateOpened}", acct#="${accountNumber}"`);
          }
        }

        // First, try to extract the dollar amount (always at the end)
        const amountMatch = !handledByComma ? trimmed.match(/\$[\d,]+(?:\.\d{2})?$/) : null;
        console.log(`  Amount match: ${amountMatch ? amountMatch[0] : 'none'}`);
        if (amountMatch) {
          fraudAmount = cleanAmount(amountMatch[0]);
          // Remove the amount from the string
          trimmed = trimmed.substring(0, trimmed.lastIndexOf(amountMatch[0])).trim();
          // Remove trailing dashes (regular hyphen, en-dash, em-dash) + whitespace
          // so the date regex (which anchors to end-of-string) can match.
          trimmed = trimmed.replace(/[-–—]+\s*$/, '').trim();
          console.log(`  After removing amount, trimmed: "${trimmed}"`);
        }
        
        // Now try to extract the date
        // Try full date first: MM-DD-YYYY or MM/DD/YYYY
        const dateMatch = !handledByComma ? trimmed.match(/[-–]?\s*(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})\s*$/) : null;
        // Also try MM-YYYY or MM/YYYY (no day)
        const monthYearMatch = !handledByComma && !dateMatch ? trimmed.match(/[-–]?\s*(\d{1,2})[-\/](\d{4})\s*$/) : null;
        // Textual month formats: "Nov. 08, 2022", "Nov 8 2022", "November 8, 2022", "Nov 2022"
        const MONTH_MAP = {
          jan:1, january:1, feb:2, february:2, mar:3, march:3, apr:4, april:4,
          may:5, jun:6, june:6, jul:7, july:7, aug:8, august:8,
          sep:9, sept:9, september:9, oct:10, october:10, nov:11, november:11, dec:12, december:12,
        };
        const textDateRe = /([-–]?\s*)(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\s*$/i;
        const textMonthYearRe = /([-–]?\s*)(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\.?,?\s+(\d{4})\s*$/i;
        const textDateMatch = !handledByComma && !dateMatch && !monthYearMatch ? trimmed.match(textDateRe) : null;
        const textMonthYearMatch = !handledByComma && !dateMatch && !monthYearMatch && !textDateMatch ? trimmed.match(textMonthYearRe) : null;
        
        if (dateMatch) {
          const month = dateMatch[1];
          const day = dateMatch[2];
          const year = dateMatch[3];
          
          // Always keep raw full date for toggle reversibility (zero-padded).
          rawFullDate = normalizeDateForFormat(`${month}-${day}-${year}`, 'full_date');
          // Render in the current toggle's canonical format.
          dateOpened = normalizeDateForFormat(rawFullDate, dateFormat);
          // Remove the date from the string
          const rawDate = dateMatch[0].replace(/^[-–]\s*/, '');
          const dateIndex = trimmed.lastIndexOf(rawDate);
          if (dateIndex > 0) {
            trimmed = trimmed.substring(0, dateIndex).trim();
          } else {
            trimmed = trimmed.replace(dateMatch[0], '').trim();
          }
          // Remove trailing dash
          trimmed = trimmed.replace(/[-–]+$/, '').trim();
        } else if (monthYearMatch) {
          // Already in MM-YYYY (no day available) — normalize to MM-YYYY zero-padded.
          dateOpened = normalizeDateForFormat(`${monthYearMatch[1]}-${monthYearMatch[2]}`, dateFormat);
          rawFullDate = dateOpened; // no day captured
          const rawDate = monthYearMatch[0].replace(/^[-–]\s*/, '');
          const dateIndex = trimmed.lastIndexOf(rawDate);
          if (dateIndex > 0) {
            trimmed = trimmed.substring(0, dateIndex).trim();
          } else {
            trimmed = trimmed.replace(monthYearMatch[0], '').trim();
          }
          trimmed = trimmed.replace(/[-–]+$/, '').trim();
        } else if (textDateMatch) {
          // "Nov. 08, 2022" / "November 8, 2022" / "Nov 8 2022"
          const monthName = textDateMatch[2].toLowerCase().replace(/\.$/, '');
          const monthNum = MONTH_MAP[monthName];
          const day = textDateMatch[3];
          const year = textDateMatch[4];
          if (monthNum) {
            rawFullDate = normalizeDateForFormat(`${monthNum}-${day}-${year}`, 'full_date');
            dateOpened = normalizeDateForFormat(rawFullDate, dateFormat);
            trimmed = trimmed.replace(textDateMatch[0], '').trim().replace(/[-–]+$/, '').trim();
          }
        } else if (textMonthYearMatch) {
          // "Nov 2022" / "November 2022"
          const monthName = textMonthYearMatch[2].toLowerCase().replace(/\.$/, '');
          const monthNum = MONTH_MAP[monthName];
          const year = textMonthYearMatch[3];
          if (monthNum) {
            dateOpened = normalizeDateForFormat(`${monthNum}-${year}`, dateFormat);
            rawFullDate = dateOpened; // no day captured
            trimmed = trimmed.replace(textMonthYearMatch[0], '').trim().replace(/[-–]+$/, '').trim();
          }
        }
        
        // What's left is the creditor name (skip if the comma-first
        // parser already produced a clean name / amount / date).
        if (!handledByComma) {
          creditorName = trimmed.replace(/[-–]+$/, '').trim();
        }
        console.log(`  Creditor name: "${creditorName}", Date: "${dateOpened}", Amount: "${fraudAmount}"`);
        
        // If we didn't find amount or date with the above method, try comma-separated
        if (!handledByComma && !fraudAmount && !dateOpened && trimmed.includes(',')) {
          const commaParts = trimmed.split(/[,\t]+/).map(p => p.trim());
          
          if (commaParts.length >= 2) {
            creditorName = commaParts[0];
            
            if (commaParts[1].includes('$') || /^\d[\d,]*$/.test(commaParts[1])) {
              fraudAmount = cleanAmount(commaParts[1]);
              dateOpened = commaParts[2] ? formatDate(commaParts[2]) : '';
            } else {
              accountNumber = commaParts[1];
              fraudAmount = commaParts[2] ? cleanAmount(commaParts[2]) : '';
              dateOpened = commaParts[3] ? formatDate(commaParts[3]) : '';
            }
          }
        }
        
        // Clean up creditor name
        creditorName = creditorName.replace(/\s*\$[\d,]+\s*$/, '').trim();
        
        // Only add if we have at least a creditor name
        if (creditorName) {
          // Use a truly unique ID: timestamp + index + random to prevent any collisions
          const uniqueId = `parsed_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;
          accounts.push({
            id: uniqueId,
            creditorName: creditorName.toUpperCase(),
            accountNumber: accountNumber,
            fraudAmount: fraudAmount,
            dateOpened: dateOpened,
            rawFullDate: rawFullDate || dateOpened,  // For toggle reversibility
          });
        }
      }
    });
    
    // NO DEDUPLICATION - User requirement: If balance is different, it's a separate account
    // All parsed accounts go through as-is
    console.log('FTCBuilderPage: Final accounts array:', accounts.length);
    console.log('FTCBuilderPage: All accounts:', JSON.stringify(accounts.map(a => ({name: a.creditorName, amount: a.fraudAmount, date: a.dateOpened, id: a.id})), null, 2));
    
    if (accounts.length > 0) {
      // Helper: reduce any date to MM-YYYY so the extractor lookup key
      // matches regardless of the current dateFormat toggle
      // (fixes Feb 2026 bug: full_date mode built MM-DD-YYYY keys and
      // never matched the extractor's MM-YYYY map, so every account
      // fell back to OTHER in the Quick Reference sidebar).
      const toMonthYearKey = (raw) => {
        if (!raw) return '';
        const s = String(raw).replace(/[\/.]/g, '-').trim();
        let mm = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
        if (mm) return `${String(parseInt(mm[1], 10)).padStart(2, '0')}-${mm[3]}`;
        mm = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (mm) return `${String(parseInt(mm[2], 10)).padStart(2, '0')}-${mm[1]}`;
        mm = s.match(/^(\d{1,2})-(\d{4})$/);
        if (mm) return `${String(parseInt(mm[1], 10)).padStart(2, '0')}-${mm[2]}`;
        return s;
      };
      // Format dates using the canonical normalizer so every output is
      // exactly "MM-YYYY" (month_year) or "MM-DD-YYYY" (full_date).
      // 2026-02-28 (user request) — Inquiries ALWAYS render with the
      // full MM-DD-YYYY date, regardless of the global dateFormat
      // toggle.  Credit bureaus reject inquiry disputes that don't
      // include the exact date of the pull, so the FTC report and
      // inquiry-removal dispute letter both need day-precision here.
      // Look up the extractor's category FIRST, then pick the date
      // format based on whether this row is an INQUIRY.
      const formatted = accounts.map(acc => {
        const originalDate = acc.rawFullDate || acc.dateOpened || '';
        // If this row came in via the AI Extractor, look up its category by
        // canonical NAME|MM-YYYY key so we don't lose the section it was in.
        let category = 'OTHER';
        if (extractorByKey.size > 0) {
          const lookupDate = toMonthYearKey(originalDate);
          const key = `${acc.creditorName.toUpperCase().trim()}|${lookupDate}`;
          const match = extractorByKey.get(key);
          if (match) {
            category = match.category || 'OTHER';
          }
        }
        // If the extractor didn't tag it, fall back to the account's own
        // hints — the ASB extractor sets isInquiry=true when the user is
        // in Inquiries mode, and the sniper extractor uses accountType.
        const isInquiry = category === 'INQUIRY'
          || category === 'INQUIRIES'
          || acc.isInquiry === true
          || (acc.accountType || '').toUpperCase() === 'INQUIRY';
        const effectiveFormat = isInquiry ? 'full_date' : dateFormat;
        const date = normalizeDateForFormat(originalDate, effectiveFormat);
        const displayName = date ? `${acc.creditorName} - ${date}` : acc.creditorName;
        // Same category lookup as above but with the extended fallback
        // that matches by name-only when the date-keyed lookup misses.
        if (extractorByKey.size > 0 && category === 'OTHER') {
          const lookupDate = toMonthYearKey(originalDate);
          const key = `${acc.creditorName.toUpperCase().trim()}|${lookupDate}`;
          const match = extractorByKey.get(key);
          if (match) {
            category = match.category || 'OTHER';
          } else {
            // Fallback: match by name only (some abbreviations / whitespace
            // drift between the extractor's account_name and the parsed
            // creditorName can drop the exact key match — the extractor
            // rarely has two accounts sharing an identical uppercase name).
            const nameOnly = acc.creditorName.toUpperCase().trim();
            for (const [k, v] of extractorByKey.entries()) {
              if (k.split('|')[0] === nameOnly) {
                category = v.category || 'OTHER';
                break;
              }
            }
          }
        }
        return {
          ...acc,
          creditorName: displayName,
          displayName: displayName,
          originalName: acc.creditorName,
          dateOpened: date,
          originalDateOpened: originalDate,
          category,
        };
      });
      setParsedAccounts(formatted);
      // Calculate total
      const total = accounts.reduce((sum, acc) => {
        const amt = parseInt((acc.fraudAmount || '').replace(/[$,]/g, '')) || 0;
        return sum + amt;
      }, 0);
      toast.success(`Parsed ${accounts.length} account(s). Total: $${total.toLocaleString()}`);
      // 2026-03-02 — abbreviation modal is now OPT-IN only.  User must
      // click the "✂️ Shorten & Merge Accounts" button in the parsed-
      // accounts toolbar to enter that flow.  The old flow (accounts
      // shown as-parsed) is preserved unchanged.
      // Feb 2026 — do NOT clear pendingExtractorAccounts here.  Re-parses
      // (e.g. user hits the button again, or the ASB → FTC round-trip
      // re-navigates) would otherwise blow away the category map and
      // every row would fall back to OTHER on the second run.  Keep the
      // stash alive for the life of this ASB-imported session.
    } else {
      toast.error('Could not parse any accounts');
    }
  };
  
  // Re-format already-parsed accounts when the date-format toggle changes.
  // Uses the canonical normalizer so output is always exactly:
  //   month_year → "MM-YYYY"     (e.g. 08-2026)
  //   full_date  → "MM-DD-YYYY"  (e.g. 08-15-2026)
  // NOTE: Called from the date-format toggle buttons (not a useEffect) to
  // avoid the react-hooks/set-state-in-effect lint warning. Reacting to
  // dateFormat via useEffect+setState is the documented antipattern here,
  // so we update both pieces of state together in a single handler.
  const handleDateFormatChange = (newFormat) => {
    setDateFormat(newFormat);
    if (parsedAccounts.length === 0) return;
    setParsedAccounts(prev => prev.map(acc => {
      const original = acc.originalName || acc.creditorName.split(' - ')[0];
      const source = acc.originalDateOpened || acc.dateOpened || '';
      // 2026-02-28 — same as the initial parse: inquiries always keep the
      // full MM-DD-YYYY date, so a global toggle to "Month & Year" for
      // regular accounts never strips the day off an inquiry row.
      const isInquiry = (acc.category || '').toUpperCase() === 'INQUIRY'
        || (acc.category || '').toUpperCase() === 'INQUIRIES'
        || acc.isInquiry === true
        || (acc.accountType || '').toUpperCase() === 'INQUIRY';
      const effectiveFormat = isInquiry ? 'full_date' : newFormat;
      const date = normalizeDateForFormat(source, effectiveFormat);
      const displayName = date ? `${original} - ${date}` : original;
      return { ...acc, creditorName: displayName, displayName, originalName: original, dateOpened: date };
    }));
  };
  
  const removeAccount = (id) => {
    setParsedAccounts(parsedAccounts.filter(a => a.id !== id));
  };

  // ============================================================
  // LOAD SAVED REPORT (when navigated via /ftc-builder/:id)
  // ============================================================
  useEffect(() => {
    if (!routeReportId) return;
    let cancelled = false;
    (async () => {
      setLoadingSavedReport(true);
      try {
        const { data } = await axios.get(`${API}/api/ftc-reports/${routeReportId}`);
        if (cancelled) return;
        // Restore client info
        const ci = data.client_info || {};
        setClientInfo({
          fullName: ci.fullName || ci.name || data.client_name || '',
          dateOfBirth: ci.dateOfBirth || ci.dob || '',
          ssn: ci.ssn || '',
          address: ci.address || '',
          city: ci.city || '',
          state: ci.state || '',
          zipCode: ci.zipCode || ci.zip_code || '',
          phone: ci.phone || '',
          email: ci.email || data.client_email || ''
        });
        // Restore the parsed accounts list (flatten across sections) while
        // preserving each account's original id so we can rebuild section
        // assignments below.
        const sec = data.sections || {};
        const flat = [];
        Object.entries(sec).forEach(([sectionNum, s]) => {
          if (s && Array.isArray(s.accounts)) {
            s.accounts.forEach((a, idx) => {
              // Stable id is critical — FTCReportBuilderNew will use this
              // exact id to repopulate sections[N].accountIds.
              const stableId = a.id || `loaded_s${sectionNum}_${idx}`;
              // Per user request 2026-06-05: legacy saved reports stored the
              // creditor name as a CSV ("NAVY FED, , $1205.00, 02/12/2018").
              // When we load them back, normalize: keep only the bare creditor
              // name, derive a clean date (MM-YYYY) and a clean balance.
              let rawCred = a.creditor_name || a.creditorName || '';
              let extractedAmount = '';
              let extractedDate = '';
              if (rawCred.includes(',')) {
                const parts = rawCred.split(',').map(p => p.trim());
                rawCred = parts[0];
                // hunt for $amount and MM/DD/YYYY in any remaining part
                for (const p of parts.slice(1)) {
                  if (!extractedAmount) {
                    const am = p.match(/\$?([\d,]+(?:\.\d{1,2})?)/);
                    if (am) extractedAmount = am[1].replace(/,/g, '').replace(/\.\d+$/, '');
                  }
                  if (!extractedDate) {
                    const dm = p.match(/(\d{1,2})[\/\-](?:(\d{1,2})[\/\-])?(\d{4})/);
                    if (dm) extractedDate = `${dm[1].padStart(2, '0')}-${dm[3]}`;
                  }
                }
              }
              // Strip any leftover " - MM-YYYY" or " - MM-DD-YYYY" suffix.
              rawCred = rawCred.replace(/\s*[-–]\s*\d{1,2}(?:-\d{1,2})?-\d{4}\s*$/, '').trim();
              flat.push({
                id: stableId,
                creditorName: rawCred,
                displayName: '', // force rebuild downstream
                originalName: rawCred,
                fraudAmount: extractedAmount || (a.balance != null ? String(a.balance) : (a.fraudAmount || '')),
                dateOpened: extractedDate || a.date_opened || a.dateFraudBegan || a.dateOpened || '',
                originalDateOpened: a.original_date_opened || a.dateOpened || a.date_opened || '',
                accountNumber: a.account_number || '',
                category: a.category || ''
              });
              a.id = stableId;
            });
          }
        });
        setParsedAccounts(flat);
        setKeptAccounts(flat);
        setSavedReportSections(sec);
        setSavedReportClientId(data.client_id || null);
        if (data.date_format) setDateFormat(data.date_format);
        // 2026-02-24 (v2) — Per user: this modal was popping up in the
        // main workflow after Credit Repair Steps.  It's now suppressed on
        // load; the user opens it explicitly via the "Edit Report" button
        // in the report header when they actually want to add/replace
        // accounts on an already-saved report.
        setStep(3);
        setEditIntent(null);
        setReplaceSectionNum(null);
        if (location.state?.openEditChoice) {
          setShowEditIntentModal(true);
          navigate(location.pathname, { replace: true, state: {} });
        } else {
          toast.success(`Loaded "${data.client_name || 'saved report'}" — tap Edit Report if you need to add or replace accounts`);
        }
        const loadedIssues = checkClient(toCheck({ fullName: ci.fullName || ci.name || data.client_name, dateOfBirth: ci.dateOfBirth || ci.dob, ssn: ci.ssn,
          address: ci.address, city: ci.city, state: ci.state, zipCode: ci.zipCode || ci.zip_code, phone: ci.phone, email: ci.email || data.client_email }))
          .filter((x) => x.level === 'error');
        if (loadedIssues.length) {
          setEditClientOpen(true);
          toast.warning(`This saved report's client info has ${loadedIssues.length} problem${loadedIssues.length > 1 ? 's' : ''} — fix them in the box at the top`);
        }
      } catch (err) {
        console.error('Failed to load saved FTC report', err);
        toast.error('Could not load that saved report — starting fresh');
        setSavedReportId(null);
        navigate('/ftc-builder', { replace: true });
      } finally {
        if (!cancelled) setLoadingSavedReport(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeReportId]);

  // ============================================================
  // PREFILL FROM ACCOUNT SUMMARY BREAKDOWN
  // When the user hits "Yes, build report" on the ASB page, they land
  // here with router state.prefillFromAsb = { text, extractorAccounts }.
  // Flow (per user request, revised Feb 2026):
  //   1. Land on Step 1 (Client Info) — user MUST fill this in first;
  //      the FTC report can't render without a client name.
  //   2. Accounts are pre-populated in state and auto-parsed silently
  //      so when the user clicks "Next" onto Step 2 (Paste Accounts)
  //      the list is already there ready to review.
  // Feb 2026: only fires when NOT loading a saved report.
  // ============================================================
  // Keep the saved report's edit choice alive across the AI-extractor round trip (it returns to /ftc-builder with no id).
  useEffect(() => {
    try {
      if (savedReportId && editIntent === 'append') {
        sessionStorage.setItem('ftc_edit_resume', JSON.stringify({ reportId: savedReportId, sections: savedReportSections, kept: keptAccounts, intent: editIntent, clientId: savedReportClientId, clientInfo, ts: Date.now() }));
      } else if (editIntent === 'edit') sessionStorage.removeItem('ftc_edit_resume');
    } catch { /* ignore */ }
  }, [savedReportId, editIntent, savedReportSections, savedReportClientId, keptAccounts, clientInfo]);

  useEffect(() => {
    if (routeReportId) return; // saved-report path wins
    const prefill = location.state?.prefillFromAsb;
    try {
      const r = JSON.parse(sessionStorage.getItem('ftc_edit_resume') || 'null');
      if (prefill && r && Date.now() - r.ts < 3 * 3600 * 1000) {
        setSavedReportId(r.reportId);
        setSavedReportSections(r.sections);
        setSavedReportClientId(r.clientId || null);
        setKeptAccounts(r.kept || []);
        if (r.clientInfo?.fullName) setClientInfo((c) => ({ ...c, ...r.clientInfo }));
        // Re-read DOB/SSN from the saved report so the extractor round trip can never blank them.
        axios.get(`${API}/api/ftc-reports/${r.reportId}`).then(({ data }) => {
          const ci = data?.client_info || {};
          setClientInfo((c) => ({ ...c, dateOfBirth: c.dateOfBirth || ci.dateOfBirth || ci.dob || '', ssn: c.ssn || ci.ssn || '' }));
        }).catch(() => {});
        setEditIntent(r.intent);
        sessionStorage.setItem('ftc_builder_append_mode', '1');
        toast.success('Back from the AI extractor — your saved report and its kept accounts are still here');
        setTimeout(() => setStep(2), 60); // review the new accounts, then Continue to Builder
      }
      sessionStorage.removeItem('ftc_edit_resume');
    } catch { /* ignore */ }

    // 2026-Feb bugfix: restore the ORIGINAL FTC client info (including DOB
    // + SSN) that we stashed before the ASB round-trip, so the user isn't
    // forced to re-paste Step 1. sessionStorage survives navigation and
    // any React remount.
    let restoredFromSession = null;
    try {
      const raw = sessionStorage.getItem('ftc_builder_pending_clientinfo');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.fullName) {
          restoredFromSession = parsed;
          setClientInfo((v) => ({ ...v, ...parsed }));
        }
        // Consume it — next visit should start clean.
        sessionStorage.removeItem('ftc_builder_pending_clientinfo');
      }
    } catch { /* ignore */ }

    if (!prefill?.text && !prefill?.clientInfo && !restoredFromSession) return;

    // Feb 2026 — 3-way FTC edit prompt introduced an APPEND mode.
    // When the user picks "Extract & ADD to existing", we stash the
    // current pastedAccountsText into sessionStorage before jumping
    // to ASB, then here we re-prepend it to whatever the extractor
    // returns so both sets show up in Step 2 for review.
    let appendMode = false;
    let priorAccountsText = '';
    try {
      appendMode = sessionStorage.getItem('ftc_builder_append_mode') === '1';
      priorAccountsText = sessionStorage.getItem('ftc_builder_existing_accounts_text') || '';
      sessionStorage.removeItem('ftc_builder_append_mode');
      sessionStorage.removeItem('ftc_builder_existing_accounts_text');
    } catch { /* ignore */ }

    if (prefill?.text) {
      // Stash the extractor's per-account category map so the orange
      // "Parse Accounts" button can look up each account's category
      // (Feb 2026 bugfix: without this, ASB → FTC lost every category).
      if (Array.isArray(prefill.extractorAccounts) && prefill.extractorAccounts.length) {
        setPendingExtractorAccounts(prefill.extractorAccounts);
      }
      if (appendMode && priorAccountsText.trim()) {
        // Merge: prior list on top, blank line, new extract below.
        // De-dupe identical lines to avoid double-listing the same
        // account when it's still on the fresh report.
        const combined = `${priorAccountsText.trim()}\n\n${prefill.text.trim()}`;
        const seen = new Set();
        const deduped = combined.split(/\r\n|\r|\n/).filter((line) => {
          const key = line.trim().toUpperCase();
          if (!key) return true;  // keep blank lines for readability
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).join('\n');
        setPastedAccountsText(deduped);
        try { toast.success('New accounts merged with your existing list — review & edit below'); } catch { /* ignore toast errors */ }
      } else {
        setPastedAccountsText(prefill.text);
      }
    }
    // Overlay ASB-supplied client-info (if any) on top of the session-restored
    // copy — this catches the case where the user edited client info while
    // on the ASB page (e.g. Smart Paste).  Only overwrite fields that ASB
    // actually provides so DOB/SSN survive.
    if (prefill?.clientInfo?.name) {
      setClientInfo((v) => ({
        ...v,
        fullName: prefill.clientInfo.name || v.fullName,
        address: prefill.clientInfo.address1 || v.address,
        city: prefill.clientInfo.city || v.city,
        state: prefill.clientInfo.state || v.state,
        zipCode: prefill.clientInfo.zip || v.zipCode,
        phone: prefill.clientInfo.phone || v.phone,
        email: prefill.clientInfo.email || v.email,
      }));
      // If ASB gave us the combined "City, ST ZIP" line, also try to split it.
      if (!prefill.clientInfo.city && prefill.clientInfo.address2) {
        const csz = extractCityStateZip(prefill.clientInfo.address2);
        setClientInfo((v) => ({
          ...v,
          city: v.city || csz.city,
          state: v.state || csz.state,
          zipCode: v.zipCode || csz.zip,
        }));
      }
    }

    // If accounts came back from ASB AND we already have a client name
    // (either from session restore or Step 1 previously) → skip Step 1
    // and land the user directly on Step 2 (Paste Accounts). We do NOT
    // auto-parse — the user clicks the orange "Parse Accounts" button
    // themselves. This matches the user's expected UX and eliminates a
    // race between the setTimeout parse and the user clicking Continue.
    const haveClientName =
      restoredFromSession?.fullName || prefill?.clientInfo?.name;
    if (prefill?.text && haveClientName) {
      setStep(2);
    } else {
      // No accounts yet OR no client name yet — stay on Step 1.
      setStep(1);
    }

    // Clear the router state so a refresh doesn't re-trigger the flow.
    navigate(location.pathname, { replace: true, state: {} });
  }, []);

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      {/* Mobile Navigation Menu */}
      <MobileNav />

      {/* 2026-02-08 — 3-way EDIT INTENT MODAL. Fires the moment a saved
          report finishes loading so the user picks up front how they want
          to edit: keep it as-is, add more accounts to an open slot, or
          replace a specific section 1-5 with a fresh set of accounts.
          Removes the earlier "just dump you into Step 3 and hope" flow. */}
      {showEditIntentModal && savedReportSections && (
        <FtcEditChoiceModal
          sections={savedReportSections}
          onEdit={() => {
            setEditIntent('edit');
            setShowEditIntentModal(false);
            setEditClientOpen(true);
            setStep(3);
            toast.success('Edit anything on the report — client info is open at the top');
          }}
          onAppend={(openSlots) => {
            setEditIntent('append');
            setShowEditIntentModal(false);
            setPastedAccountsText('');
            setParsedAccounts([]);
            setStep(1.5);
            toast.success('Old accounts kept — open the AI extractor to add new ones');
          }}
          onRemoveAndAdd={(keys) => {
            const drop = {};
            keys.forEach((k) => { const [n, i] = k.split(':'); (drop[n] = drop[n] || new Set()).add(+i); });
            const dropIds = new Set();
            setSavedReportSections((prev) => {
              const next = { ...(prev || {}) };
              Object.entries(drop).forEach(([n, idx]) => {
                const key = next[n] ? n : Number(n);
                const s = next[key] || {};
                const keep = (s.accounts || []).filter((a, i) => { if (idx.has(i)) { if (a.id) dropIds.add(a.id); return false; } return true; });
                next[key] = { ...s, accounts: keep, accountIds: keep.map((a) => a.id).filter(Boolean), ...(keep.length ? {} : { fraudType: null, customTotalAmount: '' }) };
              });
              return next;
            });
            setParsedAccounts((prev) => prev.filter((a) => !dropIds.has(a.id)));
            setKeptAccounts((prev) => prev.filter((a) => !dropIds.has(a.id)));
            setEditIntent('append');
            setShowEditIntentModal(false);
            setPastedAccountsText('');
            setStep(1.5);
            toast.success(`Removed ${keys.length} account${keys.length > 1 ? 's' : ''} — now add the new ones with the AI extractor or paste`);
          }}
          onCancel={() => { setEditIntent('edit'); setShowEditIntentModal(false); }}
        />
      )}

      {/* 2026-02-28 — Section-by-section walkthrough overlay */}
      <EditSectionsWalkthrough
        open={showSectionWalkthrough}
        onOpenChange={setShowSectionWalkthrough}
        clientId={savedReportClientId}
        sections={savedReportSections}
        onSave={(nextSections) => {
          setSavedReportSections(nextSections);
          // Treat this as "Edit as-is" so the wizard loads the modified data.
          setEditIntent('edit');
          setShowSectionWalkthrough(false);
        }}
      />

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/')} data-testid="ftc-back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            {/* Step-back button — only visible past step 1. Walks the user
                one step backward inside the wizard so they don't have to
                bounce all the way out to the dashboard. */}
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => {
                  // Walk to the previous *main* step.  1.5 is optional, so
                  // step 2 jumps straight back to step 1.
                  const prev = step === 3 ? 2 : step === 2 ? 1 : 1;
                  setStep(prev);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                data-testid="ftc-step-back-btn"
                title="Go back one step in the FTC wizard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-400" />
              <h1 className="text-2xl font-bold">FTC Report Builder</h1>
            </div>
            <ArchiveClients onRestored={() => setGalleryKey((k) => k + 1)} />
          </div>
        </div>

        {/* Step Indicator — Feb 2026: clickable pills so the user can
            jump back to any earlier step at any time (client info,
            AI extract, paste accounts) without having to click "Back"
            repeatedly.  Prevents jumping FORWARD past the current
            step so you can't skip required client info. */}
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-8 flex-wrap">
          <button
            type="button"
            onClick={() => setStep(1)}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition ${step === 1 ? 'bg-blue-600 text-white' : step > 1 ? 'bg-green-600 hover:bg-green-500 text-white cursor-pointer' : 'bg-zinc-700 text-zinc-400'}`}
            data-testid="ftc-step-pill-1"
            title="Go to Client Info"
          >
            <User className="w-4 h-4" />
            <span className="text-sm font-medium">1. Client Info</span>
          </button>
          <ArrowRight className="w-4 h-4 text-zinc-500" />
          <button
            type="button"
            onClick={() => step >= 1.5 && setStep(1.5)}
            disabled={step < 1.5}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition ${step === 1.5 ? 'bg-violet-600 text-white' : step > 1.5 ? 'bg-green-600 hover:bg-green-500 text-white cursor-pointer' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
            data-testid="ftc-step-pill-1.5"
            title={step >= 1.5 ? 'Go to AI Extract' : 'Complete Client Info first'}
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">1½. AI Extract <span className="text-[10px] uppercase opacity-70">(optional)</span></span>
          </button>
          <ArrowRight className="w-4 h-4 text-zinc-500" />
          <button
            type="button"
            onClick={() => step >= 2 && setStep(2)}
            disabled={step < 2}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition ${step === 2 ? 'bg-blue-600 text-white' : step > 2 ? 'bg-green-600 hover:bg-green-500 text-white cursor-pointer' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
            data-testid="ftc-step-pill-2"
            title={step >= 2 ? 'Go to Paste Accounts' : 'Complete previous steps first'}
          >
            <ClipboardList className="w-4 h-4" />
            <span className="text-sm font-medium">2. Paste Accounts</span>
          </button>
          <ArrowRight className="w-4 h-4 text-zinc-500" />
          <button
            type="button"
            onClick={() => (step >= 3 || savedReportSections || parsedAccounts.length > 0) && setStep(3)}
            disabled={step < 3 && !savedReportSections && !parsedAccounts.length}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full transition ${step === 3 ? 'bg-blue-600 text-white' : (savedReportSections || parsedAccounts.length) ? 'bg-zinc-700 text-zinc-200 hover:bg-zinc-600' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
            data-testid="ftc-step-pill-3"
            title={step >= 3 || savedReportSections || parsedAccounts.length ? 'Go to Build Report' : 'Complete previous steps first'}
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">3. Build Report</span>
          </button>
        </div>

        {/* Step 1: Client Info */}
        {step === 1 && (
          <>
            {/* Loading overlay if we're rehydrating a saved report */}
            {loadingSavedReport && (
              <div className="mb-6 p-6 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3 text-blue-300">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading saved report…</span>
              </div>
            )}
            {/* 2026-02-28 (user request) — the big "Start a New FTC
                Report" button now renders ABOVE the saved-reports
                gallery.  Previously the gallery came first and, once
                the user had a lot of prior reports, the green button
                was scrolled off the bottom of the page — users had to
                scroll past a long list to reach it.  Putting it up top
                keeps the primary action visible on entry. */}
            {!loadingSavedReport && !showStep1Form && (
              <div className="mt-4 mb-6 space-y-2" data-testid="ftc-start-new-prominent-wrap">
                <button
                  type="button"
                  onClick={() => {
                    setShowStep1Form(true);
                    setTimeout(() => {
                      const el = document.getElementById('ftc-step1-card');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  className="w-full py-5 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-400 hover:to-emerald-600 text-white text-lg font-bold shadow-lg shadow-emerald-900/40 transition flex items-center justify-center gap-3"
                  data-testid="ftc-start-new-prominent"
                >
                  <PlusCircle className="w-6 h-6" />
                  Start a New FTC Report
                </button>

                {/* 2026-02-28 (user request) — Upload a previous FTC
                    report PDF, duplicate it with a fresh report number,
                    delete the source, and drop the user into edit mode. */}
                <label
                  className="w-full py-4 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-700 hover:from-blue-400 hover:to-indigo-600 text-white text-base font-bold shadow-lg shadow-blue-900/40 transition flex items-center justify-center gap-3 cursor-pointer"
                  data-testid="ftc-upload-previous-btn"
                >
                  <Upload className="w-5 h-5" />
                  Upload a Previous FTC Report to Duplicate
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      try {
                        // Extract report_number from filename
                        // (`IDTheftReport_{reportNumber}.pdf` is the app format)
                        const m = f.name.match(/IDTheftReport[_\-]?(\d{8,})/i);
                        if (!m) {
                          toast.error('Could not detect a report number in this PDF filename. Only reports generated by THIS app can be uploaded (they are named IDTheftReport_XXXXXX.pdf).');
                          return;
                        }
                        const sourceReportNumber = m[1];
                        // 2026-02-28 (user follow-up) — ask before
                        // duplicating whether to regenerate the Sworn
                        // Statement + Dispute Letters with today's
                        // date, or keep the cloned copies as-is.
                        const regenerate = window.confirm(
                          'Generate FRESH Sworn Statement and Dispute Letters with today\'s date?\n\n'
                          + '✅ OK  → The clone opens with sworn/dispute cleared so you can regenerate them fresh in the wizard.\n\n'
                          + '❌ Cancel → Keep the exact sworn statement + dispute letters from the original.'
                        );
                        toast.info('Duplicating report — one moment…');
                        const r = await axios.post(`${API}/api/ftc-reports/duplicate`, {
                          source_report_number: sourceReportNumber,
                          regenerate_docs: regenerate,
                        });
                        if (r.data?.id) {
                          const msg = regenerate
                            ? `Duplicated as report #${r.data.report_number}. Sworn + Dispute cleared — regenerate them in the wizard.`
                            : `Duplicated as report #${r.data.report_number}. Opening for edit…`;
                          toast.success(msg);
                          navigate(`/ftc-builder/${r.data.id}`);
                        }
                      } catch (err) {
                        const detail = err?.response?.data?.detail || err.message;
                        toast.error(`Duplicate failed: ${detail}`);
                      }
                    }}
                  />
                </label>

                <p className="text-center text-xs text-zinc-500 mt-2">
                  Or tap any saved report below to reopen and edit
                </p>
              </div>
            )}
            {/* Saved reports gallery — only when NOT in the middle of loading one */}
            {!loadingSavedReport && (
              <SavedFTCReportsGallery
                key={galleryKey}
                onOpenReport={(rid) => {
                  setSavedReportId(rid);
                  navigate(`/ftc-builder/${rid}`, { state: { openEditChoice: true } });
                }}
                onStartNew={() => {
                  // 2026-02-08 — "Start Fresh" now actually WIPES everything
                  // (session storage + in-memory state) so the wizard opens
                  // completely blank. Previously it just scrolled the form
                  // into view, leaving the last draft populated. Also
                  // reveals the Step-1 form (which stays hidden when the
                  // user first lands here from the dashboard).
                  try {
                    sessionStorage.removeItem(PERSIST_KEY);
                    sessionStorage.removeItem('ftc_builder_pending_clientinfo');
                    sessionStorage.removeItem('ftc_builder_append_mode');
                    sessionStorage.removeItem('ftc_builder_existing_accounts_text');
                  } catch { /* ignore */ }
                  setClientInfo({
                    fullName: '', dateOfBirth: '', ssn: '', address: '',
                    city: '', state: '', zipCode: '', phone: '', email: '',
                  });
                  setPasteClientText('');
                  setPastedAccountsText('');
                  setParsedAccounts([]);
                  setPendingExtractorAccounts(null);
                  setEditIntent(null);
                  setReplaceSectionNum(null);
                  setSavedReportSections(null);
                  setSavedReportId(null);
                  setStep(1);
                  setShowStep1Form(true);
                  toast.success('Started a fresh report');
                  setTimeout(() => {
                    const el = document.getElementById('ftc-step1-card');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
              />
            )}
          {showStep1Form && (
          <Card id="ftc-step1-card" className="border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" />
                Step 1: Enter Client Information
              </CardTitle>
              <CardDescription>
                Paste all client info at once OR fill in the fields manually
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              {/* 2026-02-28 — Duplicate report early-warning banner.
                  Shows as soon as the (name, email) pair matches a
                  saved report — user can jump straight into editing
                  it instead of accidentally creating a duplicate. */}
              {existingReportMatch && (
                <div
                  className="p-3 rounded-lg bg-amber-500/10 border-2 border-amber-500/50 flex items-start justify-between gap-3"
                  data-testid="ftc-existing-report-banner"
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-amber-200">
                        A report for <span className="text-white">{existingReportMatch.client_name}</span> already exists.
                      </p>
                      <p className="text-[11px] text-amber-100/80 mt-0.5">
                        {existingReportMatch.account_count} account{existingReportMatch.account_count === 1 ? '' : 's'} · {existingReportMatch.section_count} section{existingReportMatch.section_count === 1 ? '' : 's'} · Last updated{' '}
                        {existingReportMatch.updated_at ? new Date(existingReportMatch.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'unknown'}.
                        Save now and this report will be OVERWRITTEN. Tap Open to edit it instead.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      // Navigate to the saved report route so the
                      // builder loads it and fires the edit-intent modal.
                      navigate(`/ftc-builder/${existingReportMatch.id}`);
                      setExistingReportMatch(null);
                    }}
                    className="bg-amber-500 hover:bg-amber-400 text-black font-bold flex-shrink-0"
                    data-testid="ftc-open-existing-report-btn"
                  >
                    Open existing
                  </Button>
                </div>
              )}

              {/* PASTE CLIENT INFO SECTION */}
              <div
                className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={async (e) => {
                  // 2026-02-08 — Drag/drop a PDF (or .txt) onto the box to
                  // auto-extract client info. PDFs run through PyMuPDF on
                  // the backend; text files are loaded client-side.
                  e.preventDefault(); e.stopPropagation();
                  const f = e.dataTransfer?.files?.[0];
                  if (!f) return;
                  await handleUploadClientInfoFile(f);
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardPaste className="w-5 h-5 text-blue-400" />
                  <Label className="text-blue-400 font-medium">Quick Paste — or drag a PDF here</Label>
                </div>
                <textarea
                  placeholder={`Paste client info here (any order works — the parser finds each field by shape):

RICHARD MAXA
02/11/1982
551-71-9703
12720 LAUREL ST #206
LAKESIDE CA 92040
ROCKCODRICKMAXA@GMAIL.COM`}
                  value={pasteClientText}
                  onChange={(e) => setPasteClientTextScrubbed(e.target.value)}
                  className="w-full h-40 bg-zinc-800 border border-zinc-600 text-white rounded-lg px-4 py-3 text-sm font-mono mb-3"
                  data-testid="paste-client-info-textarea"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    onClick={handleParseClientInfo}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="parse-client-info-btn"
                  >
                    <ClipboardPaste className="w-4 h-4 mr-2" />
                    Parse Client Info
                  </Button>
                  <label
                    className="cursor-pointer inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition"
                    data-testid="upload-client-info-file"
                  >
                    <Upload className="w-4 h-4" />
                    Upload PDF / TXT
                    <input
                      type="file"
                      accept=".pdf,.txt,application/pdf,text/plain"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (f) await handleUploadClientInfoFile(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                <p className="text-[10px] text-blue-200/60 mt-2 text-center">
                  Drop a PDF anywhere on this box, or use the upload button. Info goes into the textarea and auto-parses.
                </p>
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-zinc-700" />
                <span className="text-zinc-500 text-sm">OR FILL IN MANUALLY</span>
                <div className="flex-1 h-px bg-zinc-700" />
              </div>
              
              {/* 2026-02-08 — Required-field validation. When the user
                  hits Continue with anything blank, `missingFields` is
                  populated, every empty required input turns red, and
                  progression is blocked with a toast. */}
              {/* Individual Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className={missingFields.has('fullName') ? 'text-red-400' : ''}>
                    Full Legal Name * {missingFields.has('fullName') && <span className="text-red-500 ml-1">← required</span>}
                  </Label>
                  <Input
                    placeholder="John Michael Smith"
                    value={clientInfo.fullName}
                    onChange={(e) => { setClientInfo({...clientInfo, fullName: e.target.value}); if (missingFields.has('fullName')) setMissingFields(prev => { const n = new Set(prev); n.delete('fullName'); return n; }); }}
                    className={`bg-zinc-800 ${missingFields.has('fullName') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                    data-testid="client-fullname-input"
                  />
                </div>
                
                <div>
                  <Label className={missingFields.has('dateOfBirth') ? 'text-red-400' : ''}>
                    Date of Birth * {missingFields.has('dateOfBirth') && <span className="text-red-500 ml-1">← required</span>}
                  </Label>
                  <Input
                    placeholder="MM/DD/YYYY"
                    value={clientInfo.dateOfBirth}
                    onChange={(e) => { setClientInfo({...clientInfo, dateOfBirth: e.target.value}); if (missingFields.has('dateOfBirth')) setMissingFields(prev => { const n = new Set(prev); n.delete('dateOfBirth'); return n; }); }}
                    className={`bg-zinc-800 ${missingFields.has('dateOfBirth') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                    data-testid="client-dob-input"
                  />
                </div>
                
                <div>
                  <Label className={missingFields.has('ssn') ? 'text-red-400' : ''}>
                    Social Security Number * {missingFields.has('ssn') && <span className="text-red-500 ml-1">← required</span>}
                  </Label>
                  <Input
                    placeholder="XXX-XX-XXXX"
                    value={clientInfo.ssn}
                    onChange={(e) => { setClientInfo({...clientInfo, ssn: e.target.value}); missingFields.delete('ssn'); }}
                    className={`bg-zinc-800 ${missingFields.has('ssn') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                    data-testid="client-ssn-input"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className={missingFields.has('address') ? 'text-red-400' : ''}>
                    Street Address * {missingFields.has('address') && <span className="text-red-500 ml-1">← required</span>}
                  </Label>
                  <Input
                    placeholder="123 Main Street"
                    data-testid="client-address-input"
                    value={clientInfo.address}
                    onChange={(e) => { setClientInfo({...clientInfo, address: e.target.value}); if (missingFields.has('address')) setMissingFields(prev => { const n = new Set(prev); n.delete('address'); return n; }); }}
                    className={`bg-zinc-800 ${missingFields.has('address') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                  />
                </div>
                
                <div>
                  <Label className={missingFields.has('city') ? 'text-red-400' : ''}>
                    City * {missingFields.has('city') && <span className="text-red-500 ml-1">← required</span>}
                  </Label>
                  <Input
                    placeholder="Los Angeles"
                    data-testid="client-city-input"
                    value={clientInfo.city}
                    onChange={(e) => { setClientInfo({...clientInfo, city: e.target.value}); if (missingFields.has('city')) setMissingFields(prev => { const n = new Set(prev); n.delete('city'); return n; }); }}
                    className={`bg-zinc-800 ${missingFields.has('city') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className={missingFields.has('state') ? 'text-red-400' : ''}>
                      State * {missingFields.has('state') && <span className="text-red-500 ml-1">!</span>}
                    </Label>
                    <Input
                      placeholder="CA"
                    data-testid="client-state-input"
                      value={clientInfo.state}
                      onChange={(e) => { setClientInfo({...clientInfo, state: e.target.value}); if (missingFields.has('state')) setMissingFields(prev => { const n = new Set(prev); n.delete('state'); return n; }); }}
                      className={`bg-zinc-800 ${missingFields.has('state') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                    />
                  </div>
                  <div>
                    <Label className={missingFields.has('zipCode') ? 'text-red-400' : ''}>
                      ZIP Code * {missingFields.has('zipCode') && <span className="text-red-500 ml-1">!</span>}
                    </Label>
                    <Input
                      placeholder="90001"
                    data-testid="client-zip-input"
                      value={clientInfo.zipCode}
                      onChange={(e) => { setClientInfo({...clientInfo, zipCode: e.target.value}); if (missingFields.has('zipCode')) setMissingFields(prev => { const n = new Set(prev); n.delete('zipCode'); return n; }); }}
                      className={`bg-zinc-800 ${missingFields.has('zipCode') ? 'border-red-500 ring-2 ring-red-500/40' : 'border-zinc-700'}`}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Phone Number <span className="text-zinc-500 text-xs">(optional)</span></Label>
                  <Input
                    placeholder="(555) 123-4567"
                    data-testid="client-phone-input"
                    value={clientInfo.phone}
                    onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                
                <div>
                  <Label>Email Address <span className="text-zinc-500 text-xs">(optional)</span></Label>
                  <Input
                    placeholder="john@email.com"
                    data-testid="client-email-input"
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              
              <div className="mt-2"><ClientInfoCheck clientInfo={clientInfo} /></div>
              {missingFields.size > 0 && (
                <div
                  className="mt-2 rounded-lg border border-red-500/60 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                  data-testid="client-info-missing-banner"
                >
                  <strong className="uppercase tracking-wider text-red-300">
                    {missingFields.size} required field{missingFields.size > 1 ? 's' : ''} missing
                  </strong>
                  {' — '}fill them in before continuing so every dispute letter uses accurate client info.
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-zinc-700">
                <Button 
                  onClick={() => {
                    // 2026-02-08 — Validate every required field, light up
                    // the empties in red, and block Continue if anything
                    // is missing.
                    const required = ['fullName', 'dateOfBirth', 'ssn', 'address', 'city', 'state', 'zipCode'];
                    const missing = new Set();
                    for (const k of required) {
                      if (!String(clientInfo[k] || '').trim()) missing.add(k);
                    }
                    if (missing.size > 0) {
                      setMissingFields(missing);
                      toast.error(`Fill in ${missing.size} missing field${missing.size > 1 ? 's' : ''} highlighted in red`);
                      // Scroll the form back into view so the user sees the red highlights.
                      window.scrollTo({ top: document.getElementById('ftc-step1-card')?.offsetTop || 0, behavior: 'smooth' });
                      return;
                    }
                    setMissingFields(new Set());
                    setStep(1.5);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="continue-to-extract-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
          )}
          </>
        )}

        {/* Step 1.5: Open the Account Summary Breakdown extractor (optional).
            User can open the full ASB page in a new tab OR skip and paste
            accounts manually on Step 2.  Legacy AccountExtractorStep is still
            available below for backward-compat. */}
        {step === 1.5 && (
          <Card className="border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Extract Accounts
              </CardTitle>
              <CardDescription>
                Open the Account Summary Breakdown page — extract accounts from screenshots or paste-text
                using Gemini Vision. When you click <span className="text-fuchsia-400 font-semibold">Send to FTC Report Builder</span> there, you&apos;ll land back
                here on Step 2 with your accounts pre-filled and ready to review.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {editIntent === 'append' && savedReportSections && (
                <div className="p-3 rounded-lg border border-emerald-500/40 bg-emerald-950/30 text-xs text-emerald-200" data-testid="ftc-edit-append-banner">
                  <b>Editing saved report.</b> Old accounts stay in their sections. After the AI extractor you can edit or delete any old account, then choose which section each new account goes into. Then generate the new report.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={() => {
                    // Ship the client info we already have so ASB can prefill it.
                    const asbClientInfo = clientInfo?.fullName ? {
                      name: clientInfo.fullName,
                      address1: clientInfo.address,
                      address2: [clientInfo.city, clientInfo.state, clientInfo.zipCode].filter(Boolean).join(clientInfo.city && clientInfo.state ? ', ' : ' '),
                      phone: clientInfo.phone,
                      email: clientInfo.email,
                    } : null;
                    // 2026-Feb bugfix: persist the FULL FTC client info
                    // (including DOB + SSN) to sessionStorage so it survives
                    // the ASB round-trip. Without this, coming back to /ftc-builder
                    // wipes DOB/SSN and forces the user to re-paste Step 1.
                    try {
                      sessionStorage.setItem(
                        'ftc_builder_pending_clientinfo',
                        JSON.stringify(clientInfo || {}),
                      );
                    } catch { /* ignore */ }
                    navigate('/account-summary-breakdown', {
                      state: { fromFtcBuilder: true, clientInfo: asbClientInfo },
                    });
                  }}
                  className="bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 text-white"
                  data-testid="ftc-open-asb-extractor-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Open Account Summary Extractor
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200"
                  data-testid="ftc-skip-asb-extractor-btn"
                >
                  Skip — paste accounts manually
                </Button>
              </div>
              <details className="bg-zinc-900/60 border border-zinc-700 rounded-lg px-3 py-2">
                <summary className="text-xs text-zinc-400 cursor-pointer">
                  Use the legacy inline extractor
                </summary>
                <div className="pt-3">
                  <AccountExtractorStep
                    onBack={() => setStep(1)}
                    onSkip={() => setStep(2)}
                    onUseAccounts={({ text, accounts: extractorAccounts }) => {
                      setPastedAccountsText(text);
                      setStep(2);
                      setTimeout(() => parseAccounts({ overrideText: text, extractorAccounts }), 0);
                    }}
                  />
                </div>
              </details>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Client Info
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Paste Accounts */}
        {step === 2 && (
          <Card className="border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-orange-400" />
                Step 2: Paste Fraud Accounts
              </CardTitle>
              <CardDescription>
                Paste the fraud accounts below. One account per line, or comma-separated: Creditor, Account #, Amount, Date
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Format Toggle */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4" data-testid="date-format-toggle-container">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h4 className="text-white font-medium flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-blue-400" />
                      Date Format on Parsed Accounts
                    </h4>
                    <p className="text-sm text-zinc-400 mt-1">
                      {dateFormat === 'full_date'
                        ? 'Keeping the day: CAPITAL ONE 08-15-2026'
                        : 'Stripping the day: CAPITAL ONE 08-2026 (default)'}
                    </p>
                  </div>
                  <div className="flex bg-zinc-900 border border-zinc-600 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => handleDateFormatChange('full_date')}
                      data-testid="date-format-full-btn"
                      title="Keep the exact date (Month-Day-Year)"
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                        dateFormat === 'full_date'
                          ? 'bg-green-600 text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                    >
                      <CalendarDays className="w-4 h-4" />
                      Month, Day & Year
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateFormatChange('month_year')}
                      data-testid="date-format-month-year-btn"
                      title="Strip the day (Month-Year only)"
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 ${
                        dateFormat === 'month_year'
                          ? 'bg-blue-600 text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                    >
                      <Calendar className="w-4 h-4" />
                      Month & Year
                    </button>
                  </div>
                </div>
                {dateFormat === 'full_date' && (
                  <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm">
                      <strong>Full Date Mode:</strong> Each parsed account will keep the exact Month-Day-Year (e.g. <code className="bg-zinc-900 px-1 rounded">08-15-2026</code>). Click <em>Month &amp; Year</em> on the right to strip the day.
                    </p>
                  </div>
                )}
                {dateFormat === 'month_year' && (
                  <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-400 text-sm">
                      <strong>Month/Year Mode (Default):</strong> The day will be stripped from every parsed account (e.g. <code className="bg-zinc-900 px-1 rounded">08-2026</code>). Click <em>Month, Day &amp; Year</em> on the left to keep the day.
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                <Label>Paste Accounts Here</Label>
                {/* 2026-02-24 — Mode banner so the user knows exactly where
                    the pasted accounts land when coming from the Edit prompt. */}
                {editIntent === 'append' && (
                  <div className="mb-2 bg-emerald-500/10 border border-emerald-500/40 rounded-lg px-3 py-2 text-emerald-200 text-xs" data-testid="paste-mode-banner-append">
                    <strong className="text-emerald-100">Adding to existing report.</strong> Old accounts stay in their sections (you can still edit or delete them). After you continue, you choose which section each new account goes into.
                  </div>
                )}
                {editIntent === 'replace' && replaceSectionNum && (
                  <div className="mb-2 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 text-amber-200 text-xs" data-testid="paste-mode-banner-replace">
                    <strong className="text-amber-100">Replacing Section {replaceSectionNum}.</strong> The old accounts in that section have been cleared. Paste the new ones below and they will fill Section {replaceSectionNum}.
                  </div>
                )}
                <textarea
                  placeholder="CAPITAL ONE&#10;MIDLAND CREDIT MANAGEMENT&#10;SYNCHRONY BANK&#10;&#10;Or with details:&#10;CAPITAL ONE, 4567, $2500, 01/15/2023"
                  value={pastedAccountsText}
                  onChange={(e) => setPastedAccountsText(e.target.value)}
                  autoFocus={editIntent === 'append' || editIntent === 'replace'}
                  data-testid="paste-accounts-textarea"
                  className="w-full h-48 bg-zinc-800 border border-zinc-700 text-white rounded-lg px-4 py-3 text-sm font-mono"
                />
              </div>
              
              <Button onClick={parseAccounts} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="parse-accounts-btn">
                <Plus className="w-4 h-4 mr-2" />
                Parse Accounts
              </Button>
              
              {/* Debug info - shows exactly how many lines were detected */}
              {pastedAccountsText && (
                <div className="text-xs text-zinc-500 mt-1">
                  Lines detected: {pastedAccountsText.split(/\r\n|\r|\n/).filter(l => l.trim()).length}
                </div>
              )}
              
              {parsedAccounts.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label className="text-green-400">Parsed Accounts ({parsedAccounts.length}) — Edit each field below</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => { setLetterSuggestAccounts([...parsedAccounts]); setLetterSuggestOpen(true); }}
                        className="border-violet-500/50 text-violet-300 hover:bg-violet-500/10"
                        data-testid="ftc-letter-suggestions-btn"
                      >
                        ✍️ Generate all letters
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setAbbrevModalOpen(true)}
                        className="border-purple-500/50 bg-purple-500/10 text-purple-200 hover:bg-purple-500/20"
                        data-testid="open-abbrev-modal-btn"
                      >
                        <span className="mr-1.5">✂️</span>
                        Shorten &amp; Merge Accounts
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowDisputeLayoutPreview(true)}
                        className="border-amber-500/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                        data-testid="preview-dispute-layout-btn"
                      >
                        <span className="mr-1.5">👁</span>
                        Preview Dispute Letter Layout
                      </Button>
                      <span className="text-lg font-bold text-red-400">
                        Total: ${parsedAccounts.reduce((sum, acc) => {
                          const amt = parseInt((acc.fraudAmount || '').replace(/[$,]/g, '')) || 0;
                          return sum + amt;
                        }, 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-500">
                    Format: <span className="text-zinc-300">ACCOUNT NAME - MM-YYYY - $AMOUNT</span> · edit any field directly, never any commas. Tap <strong className="text-amber-300">👁 Preview Dispute Letter Layout</strong> above to confirm exactly how the accounts will be labeled and grouped in the final PDF before you generate it.
                  </p>
                  <div className="max-h-[500px] overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-zinc-600">
                    {(() => {
                      // Group parsed accounts by category so the user sees the
                      // same section layout as the AI Extractor screen.
                      // 2026-02-24 (user request) — Added APARTMENT_LEASE and
                      // RENTAL_LEASE as first-class categories on the Step 2
                      // parsed-accounts editor and the dispute-letter preview.
                      const CAT_LABELS = {
                        AUTO: 'AUTO ACCOUNTS',
                        CREDIT_CARD: 'CREDIT CARD ACCOUNTS',
                        CREDIT_UNION: 'CREDIT UNION ACCOUNTS',
                        PERSONAL_LOAN: 'PERSONAL LOAN ACCOUNTS',
                        STUDENT_LOAN: 'STUDENT LOAN ACCOUNTS',
                        MORTGAGE: 'MORTGAGE ACCOUNTS',
                        APARTMENT_LEASE: 'APARTMENT LEASE ACCOUNTS',
                        RENTAL_LEASE: 'RENTAL LEASE ACCOUNTS',
                        UTILITY: 'UTILITY ACCOUNTS',
                        MEDICAL: 'MEDICAL ACCOUNTS',
                        COLLECTION: 'COLLECTION ACCOUNTS',
                        OTHER: 'OTHER ACCOUNTS',
                      };
                      const CAT_ORDER = ['AUTO','CREDIT_CARD','CREDIT_UNION','PERSONAL_LOAN','STUDENT_LOAN','MORTGAGE','APARTMENT_LEASE','RENTAL_LEASE','UTILITY','MEDICAL','COLLECTION','OTHER'];
                      const byCat = new Map();
                      parsedAccounts.forEach((acc, idx) => {
                        const cat = acc.category || 'OTHER';
                        if (!byCat.has(cat)) byCat.set(cat, []);
                        byCat.get(cat).push({ acc, idx });
                      });
                      return CAT_ORDER.filter(c => byCat.has(c)).map(cat => (
                        <div key={cat} className="space-y-2 pt-3" data-testid={`parsed-group-${cat}`}>
                          {/* Category label — prominent bar above every group so the user
                              can visually parse (pun intended) the list the same way
                              the Account Summary Breakdown copy preview presents it. */}
                          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-900/40 to-violet-900/10 border-l-4 border-violet-500">
                            <h4 className="text-base font-black text-white tracking-wide uppercase">
                              {CAT_LABELS[cat]}
                            </h4>
                            <span className="text-xs text-violet-300 font-semibold bg-violet-900/50 px-2 py-0.5 rounded-full">
                              {byCat.get(cat).length} {byCat.get(cat).length === 1 ? 'account' : 'accounts'}
                            </span>
                          </div>
                          {byCat.get(cat).map(({ acc, idx }) => {
                      // Recombine the editable display so it always matches the canonical
                      // "NAME - DATE - $AMOUNT" output the user wants.
                      const dispName = acc.originalName || acc.creditorName.replace(/\s*-\s*\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}\s*$/, '').replace(/\s*-\s*\d{1,2}[-\/]\d{4}\s*$/, '').trim();
                      return (
                      <div key={acc.id || idx} className="p-3 bg-zinc-800 rounded-lg border border-green-500/30 space-y-2" data-testid={`parsed-acct-row-${idx}`}>
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <input
                            type="text"
                            value={dispName}
                            onChange={(e) => {
                              const updated = [...parsedAccounts];
                              const newName = (e.target.value || '').toUpperCase();
                              const dt = updated[idx].dateOpened || '';
                              updated[idx] = {
                                ...updated[idx],
                                originalName: newName,
                                creditorName: dt ? `${newName} - ${dt}` : newName,
                                displayName: dt ? `${newName} - ${dt}` : newName,
                              };
                              setParsedAccounts(updated);
                            }}
                            className="col-span-12 sm:col-span-5 px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-white text-sm font-medium"
                            placeholder="ACCOUNT NAME"
                            data-testid={`parsed-acct-name-${idx}`}
                          />
                          <input
                            type="text"
                            value={acc.dateOpened || ''}
                            onChange={(e) => {
                              const updated = [...parsedAccounts];
                              const newDate = (e.target.value || '').trim();
                              const nm = updated[idx].originalName || dispName;
                              updated[idx] = {
                                ...updated[idx],
                                dateOpened: newDate,
                                rawFullDate: newDate,
                                creditorName: newDate ? `${nm} - ${newDate}` : nm,
                                displayName: newDate ? `${nm} - ${newDate}` : nm,
                              };
                              setParsedAccounts(updated);
                            }}
                            className="col-span-6 sm:col-span-3 px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-zinc-300 text-sm text-center"
                            placeholder="MM-DD-YYYY"
                            data-testid={`parsed-acct-date-${idx}`}
                          />
                          <input
                            type="text"
                            value={acc.fraudAmount}
                            onChange={(e) => {
                              // Per user request 2026-06-05: NEVER insert
                              // thousand-separator commas in the canonical
                              // amount string. Strip anything that isn't a
                              // digit, dot, or leading $, then put back a
                              // single $ prefix.
                              const num = e.target.value.replace(/[^0-9.]/g, '');
                              const final = num ? `$${num}` : '';
                              const updated = [...parsedAccounts];
                              updated[idx] = { ...updated[idx], fraudAmount: final };
                              setParsedAccounts(updated);
                            }}
                            className="col-span-5 sm:col-span-2 px-3 py-2 bg-zinc-900 border border-zinc-600 rounded text-red-400 text-sm font-bold text-right"
                            placeholder="$0"
                            data-testid={`parsed-acct-amt-${idx}`}
                          />
                          <select
                            value={acc.category || 'OTHER'}
                            onChange={(e) => {
                              const updated = [...parsedAccounts];
                              updated[idx] = { ...updated[idx], category: e.target.value };
                              setParsedAccounts(updated);
                            }}
                            className="col-span-5 sm:col-span-1 px-1 py-2 bg-zinc-900 border border-zinc-600 rounded text-violet-300 text-[10px]"
                            data-testid={`parsed-acct-cat-${idx}`}
                            title="Move to a different section"
                          >
                            {CAT_ORDER.map(c => <option key={c} value={c}>{CAT_LABELS[c].replace(' ACCOUNTS','')}</option>)}
                          </select>
                          <Button variant="ghost" size="sm" onClick={() => removeAccount(acc.id)} className="col-span-1 p-1" data-testid={`parsed-acct-delete-${idx}`}>
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                        <p className="text-[11px] text-zinc-500 italic pl-1">
                          Preview: <span className="text-zinc-300 font-mono">{(acc.originalName || dispName)}{acc.dateOpened ? ` - ${acc.dateOpened}` : ''}{acc.fraudAmount ? ` - ${acc.fraudAmount}` : ''}</span>
                        </p>
                      </div>
                      );
                    })}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between pt-4 border-t border-zinc-700">
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Client Info
                  </Button>
                  {/* Feb 2026 — user asked for a direct route back to the AI
                      Extractor from Step 2 so they can edit / re-categorize
                      sections without walking backward through the whole
                      wizard.  Keeps the current parsed state intact — the
                      extractor's "Return to FTC Builder" will overlay a
                      fresh extractorAccounts payload when they come back.

                      2026-02-28 (user request) — When the user is in
                      APPEND mode (they picked "Add new accounts to an
                      open slot" on the edit-intent modal), signal the
                      extractor return path to MERGE rather than
                      overwrite.  Stash any text the user has already
                      typed in Step 2's textarea + set the append_mode
                      flag; the effect at line ~1015 reads both back
                      and de-dupe-merges when the extractor returns
                      accounts. */}
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (editIntent === 'append') {
                        try {
                          sessionStorage.setItem('ftc_builder_append_mode', '1');
                          sessionStorage.setItem(
                            'ftc_builder_existing_accounts_text',
                            pastedAccountsText || '',
                          );
                        } catch { /* ignore */ }
                      }
                      navigate('/account-summary-breakdown', { state: { fromFtcBuilder: true } });
                    }}
                    className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid="back-to-ai-extractor-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to AI Extractor
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setParsedAccounts([]);
                      setStep(3);
                    }}
                  >
                    Skip (Add Manually)
                  </Button>
                  {/* 2026-Feb: Dispute-letter download was moved to AFTER
                      Credit Repair emails are sent (post-email prompt in
                      FTCReportBuilderNew) so the whole flow stays linear.
                      Any inline "Download Dispute Letter" here would let
                      the user shortcut past the email step and lose the
                      LLM-drafted unique wording opportunity. */}
                  <Button 
                    onClick={() => {
                      // Text pasted / brought back from the AI extractor but never parsed → parse it now so nothing is dropped.
                      if (pastedAccountsText.trim() && (editIntent === 'append' ? !newOnes.length : !parsedAccounts.length)) parseAccounts();
                      setStep(3);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Continue to Builder
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: FTC Builder */}
        {step === 3 && (
          <Card className="border-zinc-700">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-green-400" />
                  Step 3: Build FTC Report
                </span>
                {savedReportSections && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => { setEditIntent(null); setReplaceSectionNum(null); setShowEditIntentModal(true); }}
                    className="bg-amber-600 hover:bg-amber-500 text-white font-bold"
                    data-testid="ftc-top-edit-report-btn"
                  >
                    Edit Report
                  </Button>
                )}
              </CardTitle>
              <CardDescription>
                Configure and generate the FTC Identity Theft Report
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Client Summary */}
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg" id="ftc-step3-client" data-testid="ftc-step3-client">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-bold text-blue-400">{clientInfo.fullName}</div>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditClientOpen((v) => !v)} data-testid="ftc-step3-edit-client-btn">
                    {editClientOpen ? 'Done' : '✏️ Edit client info'}
                  </Button>
                </div>
                {editClientOpen && <div className="my-2"><ClientInfoEditor clientInfo={clientInfo} setClientInfo={setClientInfo} /></div>}
                <div className="text-sm text-zinc-400">
                  {clientInfo.address && `${clientInfo.address}, `}{clientInfo.city}, {clientInfo.state} {clientInfo.zipCode}
                </div>
                {parsedAccounts.length > 0 && (
                  <div className="text-sm text-green-400 mt-1">
                    {editIntent === 'append'
                      ? `${keptAccounts.length} account(s) kept on the report + ${newOnes.length} new`
                      : savedReportSections ? `${parsedAccounts.length} account(s) on this report` : `${parsedAccounts.length} account(s) ready to add`}
                  </div>
                )}
              </div>

              {/* 2026-02-24 (v2) — Simple edit toolbar (only shows when a
                  saved report was loaded).  Replaces the old "How do you
                  want to edit this report?" modal.  Two big buttons:
                    • Add via Paste   → jumps to Step 2 in append mode
                    • Add via AI      → jumps to Step 1.5 extractor
                  New accounts flow into any open slots automatically. */}
              {savedReportSections && (
                <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/40 rounded-lg" data-testid="ftc-edit-toolbar">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <div className="font-semibold text-emerald-300">✏️ Editing a saved report</div>
                      <div className="text-emerald-200/70 text-xs">
                        Paste more accounts or run the AI extractor — then edit or delete old accounts and choose a section for each new one.
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditIntent('append');
                          setStep(2);
                          setPastedAccountsText('');
                          toast.info('Paste new accounts below — then choose a section for each one');
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                        data-testid="ftc-edit-add-paste-btn"
                      >
                        📋 Add via Paste
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditIntent('append');
                          setStep(1.5);
                          toast.info('Run the AI extractor — then choose a section for each one');
                        }}
                        className="bg-violet-600 hover:bg-violet-700 text-white h-8"
                        data-testid="ftc-edit-add-ai-btn"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        Add via AI Extractor
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* FTC Report Builder - EXACT SAME as Dispute Wizard */}
              
              {/* Theme Toggle */}
              <div className="mb-6 p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                <Label className="text-sm font-semibold text-zinc-300 mb-3 block">FTC Report Style</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ftcTheme"
                      value="bw"
                      checked={ftcTheme === 'bw'}
                      onChange={(e) => setFtcTheme(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-zinc-300 text-sm">Black & White</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ftcTheme"
                      value="color"
                      checked={ftcTheme === 'color'}
                      onChange={(e) => setFtcTheme(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-zinc-300 text-sm">Official Color - Default (FTC-style)</span>
                  </label>
                </div>
              </div>
              
              {/* 2026-Feb: Parsed accounts DO flow to the builder — they
                  appear in the Quick Reference / Unassigned panel so the
                  user can select which ones to add to each of the five
                  FTC sections manually. Nothing is auto-assigned to a
                  section — the user picks. */}

              {lockedSlots.length > 0 && (
                <div className="mb-3 p-3 rounded-lg border border-emerald-500/40 bg-emerald-950/30 text-xs text-emerald-200" data-testid="ftc-append-place-banner">
                  <b>Old accounts stay in their sections</b> ({lockedSlots.join(', ')}). Use the pencil to edit one or the trash to delete it. Then tap <b>Add</b> or <b>Edit</b> on any section, pick the fraud type, and tick the new accounts that go there.
                </div>
              )}
              <FTCReportBuilderNew 
                clientId={null}
                savedReportId={savedReportId}
                // 2026-02-08 — user requested the 3-way "How do you want
                // to edit this report?" prompt should ONLY fire from an
                // explicit Edit Report click on a saved report, not on
                // load. We pass in a flag + callback so the builder can
                // render an "Edit Report" button that opens the intent
                // modal owned by this page.
                canOpenEditIntent={!!savedReportSections}
                onClientInfoEdited={(ci) => setClientInfo((c) => ({ ...c, fullName: ci.name ?? c.fullName, dateOfBirth: ci.dob ?? c.dateOfBirth, ssn: ci.ssn ?? c.ssn,
                  address: ci.address ?? c.address, city: ci.city ?? c.city, state: ci.state ?? c.state, zipCode: ci.zip_code ?? c.zipCode, phone: ci.phone ?? c.phone, email: ci.email ?? c.email }))}
                onEditClientInfo={() => {
                  setEditClientOpen(true);
                  setTimeout(() => document.getElementById('ftc-step3-client')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                }}
                onOpenEditIntent={() => {
                  setEditIntent(null);
                  setReplaceSectionNum(null);
                  setShowEditIntentModal(true);
                }}
                onReportSaved={(newId) => {
                  if (newId && newId !== savedReportId) {
                    setSavedReportId(newId);
                    // Update the URL so a refresh keeps editing the same report
                    navigate(`/ftc-builder/${newId}`, { replace: true });
                  }
                }}
                clientData={{
                  name: clientInfo.fullName,
                  dob: clientInfo.dateOfBirth,
                  ssn: clientInfo.ssn,
                  address: clientInfo.address,
                  city: clientInfo.city,
                  state: clientInfo.state,
                  zip_code: clientInfo.zipCode,
                  phone: clientInfo.phone,
                  email: clientInfo.email
                }}
                signatureData={null}
                preloadedAccounts={builderAccounts}
                preloadedSections={builderSections}
                ftcTheme={ftcTheme}
                dateFormat={dateFormat}
                onComplete={() => {
                  toast.success('FTC Report created!');
                }}
                // Suggested letters pop up right after the sworn statement is created.
                onSwornStatementCreated={() => {
                  if (!parsedAccounts.length) return;
                  setLetterSuggestAccounts([...parsedAccounts]);
                  setLetterSuggestOpen(true);
                }}
              />
              
              <div className="flex justify-start mt-4 pt-4 border-t border-zinc-700">
                <Button variant="ghost" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Paste Accounts
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Dispute Letter Layout Preview modal (Feb 2026 UX request) ──
          Renders parsed accounts in the exact same visual format the
          final dispute-letter PDF will use — bold uppercase category
          header + bold account name — MM-DD-YYYY — $amount bullets.
          Category words that appear inside account names are stripped
          under the correct header so nothing is duplicated (matches
          backend dispute_letter._strip_category_from_name). */}
      <DisputeLayoutPreviewModal
        open={showDisputeLayoutPreview}
        onClose={() => setShowDisputeLayoutPreview(false)}
        parsedAccounts={parsedAccounts}
        clientName={clientInfo?.fullName || clientInfo?.name || ""}
      />
      {/* 2026-03-01 — Shorten & Merge modal (opens auto after parse, and
          manually via the toolbar button on parsed-accounts panel). */}
      <AccountAbbreviationModal
        open={abbrevModalOpen}
        onOpenChange={setAbbrevModalOpen}
        accounts={parsedAccounts}
        onApply={(transformed) => {
          setParsedAccounts(transformed);
        }}
      />
      <BatchLettersModal open={letterSuggestOpen} accounts={letterSuggestAccounts} onClose={() => setLetterSuggestOpen(false)}
        clientName={clientInfo?.fullName || ''}
        clientAddressLines={[clientInfo?.address, [clientInfo?.city, clientInfo?.state, clientInfo?.zipCode].filter(Boolean).join(', ')].filter(Boolean)} />
    </div>
  );
}

// ─── DisputeLayoutPreviewModal ──────────────────────────────────────────
// Shows how the accounts will look in the final dispute letter PDF.  Bold
// category header (uppercase) + bulleted rows with the account NAME in
// bold, followed by date and balance.  Redundant category words are
// stripped from account names so nothing appears twice.
function DisputeLayoutPreviewModal({ open, onClose, parsedAccounts, clientName }) {
  if (!open) return null;

  const CAT_LABELS = {
    AUTO: "Auto Loan",
    CREDIT_CARD: "Credit Card",
    CREDIT_UNION: "Credit Union",
    PERSONAL_LOAN: "Personal Loan",
    STUDENT_LOAN: "Student Loan",
    MORTGAGE: "Mortgage",
    APARTMENT_LEASE: "Apartment Lease",
    RENTAL_LEASE: "Rental Lease",
    UTILITY: "Utility",
    MEDICAL: "Medical",
    COLLECTION: "Collection",
    OTHER: "",  // OTHER renders without a category header
  };
  const CAT_ORDER = ["AUTO","CREDIT_CARD","CREDIT_UNION","PERSONAL_LOAN","STUDENT_LOAN","MORTGAGE","APARTMENT_LEASE","RENTAL_LEASE","UTILITY","MEDICAL","COLLECTION","OTHER"];

  const stripCategoryFromName = (name, label) => {
    if (!name || !label) return name || "";
    const n = String(name).trim();
    // Suffix strip: "CAPITAL ONE CREDIT CARD" → "CAPITAL ONE"
    const suffix = new RegExp(`[\\s\\-\u2013\u2014,:/]+${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b\\.?$`, "i");
    let m = n.match(suffix);
    if (m) {
      const stripped = n.substring(0, m.index).replace(/[\s\t\-,:/\u2013\u2014]+$/, "");
      if (stripped) return stripped;
    }
    // Prefix strip: "CREDIT CARD - CAPITAL ONE" → "CAPITAL ONE"
    const prefix = new RegExp(`^${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[\\s\\-\u2013\u2014,:/]+`, "i");
    m = n.match(prefix);
    if (m) {
      const stripped = n.substring(m[0].length).replace(/^[\s\t\-,:/\u2013\u2014]+/, "");
      if (stripped) return stripped;
    }
    return n;
  };

  const byCat = new Map();
  parsedAccounts.forEach((acc) => {
    const cat = acc.category || "OTHER";
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(acc);
  });

  const totalAccts = parsedAccounts.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="dispute-layout-preview-modal">
      <div className="max-w-3xl w-full max-h-[90vh] rounded-2xl border border-amber-500/40 bg-gradient-to-b from-zinc-900 to-zinc-950 shadow-2xl shadow-amber-500/10 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-zinc-800 bg-gradient-to-r from-amber-900/30 via-zinc-950 to-orange-900/30">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <span>👁</span> Dispute Letter Layout Preview
            </h2>
            <p className="text-xs text-zinc-400 mt-0.5">
              This is EXACTLY how {totalAccts} account{totalAccts === 1 ? "" : "s"} will appear in the final PDF.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white flex items-center justify-center flex-shrink-0"
            data-testid="preview-modal-close"
            aria-label="Close preview"
          >
            <span className="text-lg">×</span>
          </button>
        </div>

        {/* Body — mimics the PDF's dispute letter layout */}
        <div className="flex-1 overflow-y-auto px-5 py-5 bg-white">
          <div className="max-w-2xl mx-auto text-black font-serif">
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-2">Preview only — not the actual PDF</p>
            <h3 className="text-sm font-black mb-3">List of Disputed Accounts:</h3>
            {totalAccts === 0 ? (
              <p className="italic text-slate-500">(No accounts yet — go back and paste some accounts first.)</p>
            ) : (
              CAT_ORDER.filter((c) => byCat.has(c)).map((cat) => {
                const label = CAT_LABELS[cat];
                const items = byCat.get(cat);
                return (
                  <div key={cat} className="mb-4">
                    {label ? (
                      <p className="font-black text-black uppercase text-[12px] tracking-wider mt-3 mb-1">
                        {label}
                      </p>
                    ) : null}
                    <ul className="list-disc pl-6 space-y-1">
                      {items.map((acc, i) => {
                        const rawName = (acc.originalName || acc.creditorName || "").replace(/\s*-\s*\d{1,2}[-/]\d{1,2}[-/]\d{2,4}\s*$/, "").replace(/\s*-\s*\d{1,2}[-/]\d{4}\s*$/, "").trim();
                        const stripped = stripCategoryFromName(rawName, label);
                        const parts = [];
                        if (acc.dateOpened) parts.push(acc.dateOpened);
                        if (acc.fraudAmount) parts.push(acc.fraudAmount);
                        return (
                          <li key={acc.id || i} className="text-[13px] leading-snug" data-testid={`preview-row-${cat}-${i}`}>
                            <strong className="font-bold text-black">{stripped || rawName}</strong>
                            {parts.length > 0 ? ` — ${parts.join(" — ")}` : ""}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
            {clientName ? (
              <p className="mt-6 text-xs text-slate-500 italic">
                Signed: {clientName}
              </p>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-zinc-800 bg-zinc-950">
          <p className="text-[11px] text-zinc-500">
            Change any category with the dropdown on each row below to fix the grouping.
          </p>
          <Button
            onClick={onClose}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="preview-modal-done"
          >
            Looks Good — Close
          </Button>
        </div>
      </div>
    </div>
  );
}

